/*
 * Priority Talk Call Center — Standalone App
 *
 * This module is mounted as the ENTIRE React app when
 * REACT_APP_MODE=call_center.  It renders its own login, its own
 * layout, and its own routes — NOTHING from Command Center is
 * imported.  All API calls go to the same Priority Command backend
 * (URL is provided at deploy-time via window.__API_BASE_URL).
 *
 * Routes:
 *   /                        -> unified admin/operator login
 *   /signup                  -> public company self-registration
 *   /master/login            -> master (platform owner) login
 *   /master                  -> master dashboard (all tenants overview)
 *   /admin                   -> tenant admin dashboard
 *   /operator                -> operator auto-dialer shift page
 *
 * All UI text is in English.
 */
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import JsSIP from 'jssip';
import { Phone, PhoneCall, ListChecks, FileText, Trophy, ShoppingCart, LogOut, Loader2, PlayCircle, SkipForward, PhoneOff, Upload, MessageCircle, Send, Mic, MicOff, Settings, Radio, AlertCircle, CheckCircle2, ShieldCheck, Building2, DollarSign, XCircle, Hash, Users, Ban, Play, Rocket, ArrowRight, ArrowLeft, Circle, Shield, Activity, Zap, Globe, Trash2 } from 'lucide-react';
import API_URL from '../../utils/apiUrl';

// Silence JsSIP verbose logs in production
JsSIP.debug.disable();

// Build a WS URL from the current API_URL (respects http/https)
const WS_BASE = (typeof window !== 'undefined')
  ? (API_URL
      ? API_URL.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`)
  : '';

// ---- Token helpers ----
const TOKEN_KEY = 'ptcc_token';
const ROLE_KEY = 'ptcc_role';
const MASTER_TOKEN_KEY = 'ptcc_master_token';
const getToken = () => localStorage.getItem(TOKEN_KEY);
const getRole = () => localStorage.getItem(ROLE_KEY);
const getMasterToken = () => localStorage.getItem(MASTER_TOKEN_KEY);
const setSession = (token, role) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, role);
};
const setMasterSession = (token) => localStorage.setItem(MASTER_TOKEN_KEY, token);
const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
};
const clearMasterSession = () => localStorage.removeItem(MASTER_TOKEN_KEY);
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });
const masterHdr = () => ({ Authorization: `Bearer ${getMasterToken()}` });

const fmtMin = (sec) => `${Math.floor((sec || 0) / 60)} min ${(sec || 0) % 60}s`;
const fmtMSS = (sec) => {
  const s = Math.max(0, sec | 0);
  return `${String((s / 60) | 0).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

// -----------------------------------------------------------------------
// LOGIN (tenant admin / operator)
// -----------------------------------------------------------------------
function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/auth/admin/login`,
        { email: email.trim(), password });
      const admin = res.data.admin || {};
      setSession(res.data.token, admin.role || 'operator');
      nav(admin.role === 'super_admin' ? '/admin' : '/operator', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-full max-w-md shadow-2xl" data-testid="ptcc-login">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-600 mb-3">
            <PhoneCall className="w-7 h-7 text-white"/>
          </div>
          <h1 className="text-2xl font-semibold text-white">Priority Talk</h1>
          <p className="text-slate-400 text-sm">Call Center — Sign in</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="text"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email or username"
            className="w-full px-3 py-2.5 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-emerald-500 focus:outline-none"
            data-testid="ptcc-email"
            required
            autoFocus
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-3 py-2.5 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-emerald-500 focus:outline-none"
            data-testid="ptcc-password"
            required
          />
          {error && <div className="text-red-400 text-sm p-2 bg-red-500/10 border border-red-500/20 rounded" data-testid="ptcc-login-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="ptcc-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
            Sign in
          </button>
        </form>
        <div className="mt-5 pt-4 border-t border-slate-700 space-y-2 text-center text-sm">
          <div>
            <span className="text-slate-400">New company?</span>{' '}
            <Link to="/signup" className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="ptcc-signup-link">
              Register your business
            </Link>
          </div>
          <div>
            <Link to="/master/login" className="text-slate-500 hover:text-slate-300 text-xs inline-flex items-center gap-1" data-testid="ptcc-master-link">
              <ShieldCheck className="w-3 h-3"/> Platform owner login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// PUBLIC SIGNUP — company self-registration
// -----------------------------------------------------------------------
function Signup() {
  const nav = useNavigate();
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [signerName, setSignerName] = useState('');
  const [region, setRegion] = useState('EU');
  const [acceptDpa, setAcceptDpa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!acceptDpa) { setError('You must accept the Data Processing Agreement'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API_URL}/api/onboarding/signup`, {
        company_name: company.trim(),
        owner_email: email.trim().toLowerCase(),
        password,
        data_region: region,
        accepted_dpa: true,
        signer_name: signerName.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-full max-w-md text-center" data-testid="ptcc-signup-done">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500 mb-3">
            <ShieldCheck className="w-7 h-7 text-white"/>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Awaiting approval</h1>
          <p className="text-slate-400 text-sm mb-4">
            Your company registration has been received. The platform owner will review your
            application and assign you a dedicated phone number. You will be able to sign in
            once your account is approved.
          </p>
          <button onClick={() => nav('/')} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="ptcc-signup-back">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 py-8">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-full max-w-md shadow-2xl" data-testid="ptcc-signup">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-600 mb-3">
            <Building2 className="w-7 h-7 text-white"/>
          </div>
          <h1 className="text-2xl font-semibold text-white">Register your business</h1>
          <p className="text-slate-400 text-sm">Create your Priority Talk company account</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <FormField label="Company name" value={company} onChange={setCompany} placeholder="Acme AS" testid="ptcc-signup-company" required/>
          <FormField label="Owner email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" testid="ptcc-signup-email" required/>
          <FormField label="Signer full name" value={signerName} onChange={setSignerName} placeholder="Jane Doe" testid="ptcc-signup-signer" required/>
          <FormField label="Password (min 8 chars)" type="password" value={password} onChange={setPassword} testid="ptcc-signup-password" required/>
          <FormField label="Confirm password" type="password" value={password2} onChange={setPassword2} testid="ptcc-signup-password2" required/>
          <div>
            <label className="block text-xs uppercase text-slate-400 mb-1">Data region</label>
            <select value={region} onChange={e => setRegion(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-700 text-white rounded-lg border border-slate-600"
              data-testid="ptcc-signup-region">
              <option value="EU">EU (GDPR)</option>
              <option value="US">US</option>
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={acceptDpa} onChange={e => setAcceptDpa(e.target.checked)}
              className="mt-1" data-testid="ptcc-signup-dpa"/>
            <span>I accept the Data Processing Agreement and confirm I have authority to sign on behalf of the company.</span>
          </label>
          {error && <div className="text-red-400 text-sm p-2 bg-red-500/10 border border-red-500/20 rounded" data-testid="ptcc-signup-error">{error}</div>}
          <button type="submit" disabled={loading}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="ptcc-signup-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
            Submit for approval
          </button>
        </form>
        <p className="text-xs text-slate-500 text-center mt-5">
          Already have an account? <Link to="/" className="text-emerald-400 hover:text-emerald-300">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', placeholder, testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2.5 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-emerald-500 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// MASTER LOGIN
// -----------------------------------------------------------------------
function MasterLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/master/login`,
        { email: email.trim(), password });
      setMasterSession(res.data.token);
      nav('/master', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Master login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 w-full max-w-md shadow-2xl" data-testid="ptcc-master-login">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500 mb-3">
            <ShieldCheck className="w-7 h-7 text-white"/>
          </div>
          <h1 className="text-2xl font-semibold text-white">Master Console</h1>
          <p className="text-slate-400 text-sm">Platform Owner — Priority Talk</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input type="text" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Master email or username" required autoFocus
            autoComplete="username"
            className="w-full px-3 py-2.5 bg-slate-800 text-white rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none"
            data-testid="ptcc-master-email"/>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Master password" required
            className="w-full px-3 py-2.5 bg-slate-800 text-white rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none"
            data-testid="ptcc-master-password"/>
          {error && <div className="text-red-400 text-sm p-2 bg-red-500/10 border border-red-500/20 rounded" data-testid="ptcc-master-login-error">{error}</div>}
          <button type="submit" disabled={loading}
            className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="ptcc-master-login-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
            Enter Master Console
          </button>
        </form>
        <p className="text-xs text-slate-600 text-center mt-6">
          <Link to="/" className="hover:text-slate-400">← Back to tenant login</Link>
        </p>
      </div>
    </div>
  );
}

function MasterGuard({ children }) {
  const loc = useLocation();
  if (!getMasterToken()) return <Navigate to="/master/login" state={{ from: loc }} replace/>;
  return children;
}

// -----------------------------------------------------------------------
// MASTER DASHBOARD — all tenants overview + approval + DID assignment
// -----------------------------------------------------------------------
function MasterDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API_URL}/api/master/call/dashboard`, { headers: masterHdr() }),
        axios.get(`${API_URL}/api/master/call/tenants`, { headers: masterHdr() }),
      ]);
      setStats(d.data);
      setTenants(t.data.tenants || []);
    } catch (e) {
      if (e.response?.status === 401) { clearMasterSession(); nav('/master/login'); return; }
      setErr(e.response?.data?.detail || 'Failed to load master dashboard');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const logout = () => { clearMasterSession(); nav('/master/login', { replace: true }); };

  const filtered = tenants.filter(t => filter === 'all' ? true : (t.approval_status || 'pending') === filter);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between" data-testid="ptcc-master-header">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-400"/>
          <span className="font-semibold">Priority Talk — Master Console</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/master/reseller')} className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid="master-reseller-link">
            <Phone className="w-4 h-4"/> Reseller
          </button>
          <button onClick={() => nav('/master/reports')} className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid="master-reports-link">
            <FileText className="w-4 h-4"/> Weekly reports
          </button>
          <button onClick={() => nav('/master/security')} className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="master-security-link">
            <Shield className="w-4 h-4"/> Security Center
          </button>
          <button onClick={logout} className="text-sm text-slate-300 hover:text-white flex items-center gap-1" data-testid="ptcc-master-logout">
            <LogOut className="w-4 h-4"/> Sign out
          </button>
        </div>
      </header>
      <main className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <Building2 className="w-6 h-6"/> All companies
        </h1>
        <p className="text-sm text-slate-500 mb-6">Review self-registered companies, approve them, and assign a unique phone number.</p>

        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded" data-testid="ptcc-master-error">{err}</div>}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <MStat label="Total" value={stats?.tenants?.total ?? '—'} icon={Building2} testid="mstat-total"/>
          <MStat label="Pending" value={stats?.tenants?.pending ?? '—'} icon={AlertCircle} color="text-amber-600" testid="mstat-pending"/>
          <MStat label="Approved" value={stats?.tenants?.approved ?? '—'} icon={CheckCircle2} color="text-emerald-600" testid="mstat-approved"/>
          <MStat label="Suspended" value={stats?.tenants?.suspended ?? '—'} icon={Ban} color="text-red-600" testid="mstat-suspended"/>
          <MStat label="Numbers assigned" value={stats?.tenants?.with_did ?? '—'} icon={Hash} testid="mstat-did"/>
          <MStat label="Active subscriptions" value={stats?.tenants?.active_subscriptions ?? '—'} icon={DollarSign} color="text-emerald-600" testid="mstat-subs"/>
        </div>

        {/* Revenue */}
        {stats?.revenue?.length > 0 && (
          <div className="bg-white rounded-lg border shadow-sm p-4 mb-6" data-testid="ptcc-master-revenue">
            <h2 className="font-medium mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4"/> Revenue (paid bundle orders)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {stats.revenue.map(r => (
                <div key={r.currency} className="p-3 bg-emerald-50 rounded border border-emerald-200">
                  <div className="text-xs text-slate-500">{r.orders} orders</div>
                  <div className="text-lg font-semibold text-emerald-700">{r.total.toFixed(2)} {r.currency}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-3">
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded text-sm ${filter === f ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600 hover:bg-slate-50'}`}
              data-testid={`filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Tenants table */}
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400"/> : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                <tr>
                  <th className="p-3 text-left">Company</th>
                  <th className="p-3 text-left">Owner</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Subscription</th>
                  <th className="p-3 text-left">Phone number</th>
                  <th className="p-3 text-right">Operators</th>
                  <th className="p-3 text-left">Registered</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="ptcc-master-tenants-tbody">
                {filtered.length === 0 && (
                  <tr><td colSpan="8" className="p-8 text-center text-slate-400">No companies match this filter.</td></tr>
                )}
                {filtered.map(t => (
                  <TenantRow key={t.tenant_id} t={t} onChange={load}/>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function MStat({ label, value, icon: Icon, color = 'text-slate-800', testid }) {
  return (
    <div className="bg-white rounded-lg border shadow-sm p-3">
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function TenantRow({ t, onChange }) {
  const [busy, setBusy] = useState(false);
  const [phoneEdit, setPhoneEdit] = useState(false);
  const [phone, setPhone] = useState(t.phone_number || '');
  const [err, setErr] = useState(null);

  const approve = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/tenants/${t.tenant_id}/approve`, {}, { headers: masterHdr() });
      await onChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/tenants/${t.tenant_id}/reject`, { reason }, { headers: masterHdr() });
      await onChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };
  const suspend = async () => {
    if (!window.confirm(`Suspend "${t.tenant_name}"? Their operators will lose access.`)) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/tenants/${t.tenant_id}/freeze`, { reason: 'master action' }, { headers: masterHdr() });
      await onChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Suspend failed'); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/tenants/${t.tenant_id}/activate`, {}, { headers: masterHdr() });
      await onChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Activate failed'); }
    finally { setBusy(false); }
  };
  const savePhone = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.patch(`${API_URL}/api/master/call/tenants/${t.tenant_id}/phone`,
        { phone_number: phone.trim() }, { headers: masterHdr() });
      setPhoneEdit(false);
      await onChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Assign phone failed'); }
    finally { setBusy(false); }
  };

  const statusPill = {
    pending:  'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
  }[t.approval_status] || 'bg-slate-100 text-slate-600';

  return (
    <tr className="border-t hover:bg-slate-50" data-testid={`tenant-row-${t.tenant_id}`}>
      <td className="p-3 font-medium">{t.tenant_name}</td>
      <td className="p-3 text-slate-600 text-xs font-mono">{t.owner_email}</td>
      <td className="p-3">
        <span className={`px-2 py-0.5 rounded text-xs ${statusPill}`} data-testid={`tenant-status-${t.tenant_id}`}>
          {t.approval_status || 'pending'}
        </span>
        {t.status === 'frozen' && (
          <span className="ml-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">SUSPENDED</span>
        )}
      </td>
      <td className="p-3 text-xs">
        <div>{t.plan || '—'}</div>
        <div className="text-slate-500">{t.subscription_status || 'no subscription'}</div>
      </td>
      <td className="p-3 font-mono text-xs">
        {phoneEdit ? (
          <div className="flex items-center gap-1">
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+4712345678"
              className="px-2 py-1 border rounded text-xs w-32"
              data-testid={`phone-input-${t.tenant_id}`}/>
            <button onClick={savePhone} disabled={busy} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`phone-save-${t.tenant_id}`}>Save</button>
            <button onClick={() => { setPhoneEdit(false); setPhone(t.phone_number || ''); }} className="px-2 py-1 border rounded text-xs">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setPhoneEdit(true)} className="hover:underline text-left" data-testid={`phone-edit-${t.tenant_id}`}>
            {t.phone_number || <span className="text-slate-400 italic">— not assigned —</span>}
          </button>
        )}
      </td>
      <td className="p-3 text-right">{t.operator_count}</td>
      <td className="p-3 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
      <td className="p-3 text-right">
        <div className="flex justify-end gap-1 flex-wrap">
          {t.approval_status === 'pending' && (
            <>
              <button onClick={approve} disabled={busy} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs flex items-center gap-1" data-testid={`approve-${t.tenant_id}`}>
                <CheckCircle2 className="w-3 h-3"/> Approve
              </button>
              <button onClick={reject} disabled={busy} className="px-2 py-1 bg-red-500 text-white rounded text-xs flex items-center gap-1" data-testid={`reject-${t.tenant_id}`}>
                <XCircle className="w-3 h-3"/> Reject
              </button>
            </>
          )}
          {t.approval_status === 'approved' && t.status !== 'frozen' && (
            <button onClick={suspend} disabled={busy} className="px-2 py-1 bg-red-500 text-white rounded text-xs flex items-center gap-1" data-testid={`suspend-${t.tenant_id}`}>
              <Ban className="w-3 h-3"/> Suspend
            </button>
          )}
          {t.status === 'frozen' && (
            <button onClick={activate} disabled={busy} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs flex items-center gap-1" data-testid={`activate-${t.tenant_id}`}>
              <Play className="w-3 h-3"/> Reactivate
            </button>
          )}
        </div>
        {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------
// LAYOUT (tenant admin/operator)
// -----------------------------------------------------------------------
function Shell({ children, current }) {
  const nav = useNavigate();
  const role = getRole();
  const items = role === 'super_admin' ? [
    { to: '/admin', icon: FileText, label: 'Overview' },
    { to: '/admin/monitor', icon: Radio, label: 'Live monitor' },
    { to: '/admin/lists', icon: ListChecks, label: 'Phone lists' },
    { to: '/admin/records', icon: Phone, label: 'Call log' },
    { to: '/admin/leaderboard', icon: Trophy, label: 'Leaderboard' },
    { to: '/admin/team', icon: Users, label: 'Operators' },
    { to: '/admin/sip', icon: Settings, label: 'SIP config' },
    { to: '/team-chat', icon: MessageCircle, label: 'Team chat' },
    { to: '/operator', icon: PhoneCall, label: 'Operator shift' },
  ] : [
    { to: '/operator', icon: PhoneCall, label: 'Operator shift' },
    { to: '/team-chat', icon: MessageCircle, label: 'Team chat' },
  ];
  const logout = () => { clearSession(); nav('/', { replace: true }); };
  return (
    <div className="min-h-screen flex bg-slate-100">
      <aside className="w-56 bg-slate-900 text-white flex flex-col" data-testid="ptcc-sidebar">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-400"/>
            <span className="font-semibold">Priority Talk</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Call Center</p>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {items.map(i => {
            const Icon = i.icon;
            const active = current === i.to;
            return (
              <Link
                key={i.to}
                to={i.to}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${active ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
                data-testid={`nav-${i.to.replace(/\//g, '-')}`}
              >
                <Icon className="w-4 h-4"/> {i.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="ptcc-logout">
            <LogOut className="w-4 h-4"/> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}

function Guard({ children, requireRole }) {
  const loc = useLocation();
  if (!getToken()) return <Navigate to="/" state={{ from: loc }} replace/>;
  if (requireRole === 'super_admin' && getRole() !== 'super_admin') {
    return <Navigate to="/operator" replace/>;
  }
  return children;
}

// -----------------------------------------------------------------------
// ADMIN — Overview
// -----------------------------------------------------------------------
function Overview() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // Check onboarding status first — if incomplete, redirect to wizard
        try {
          const ob = await axios.get(`${API_URL}/api/admin/call/onboarding`, { headers: authHdr() });
          if (!ob.data.complete) {
            nav('/admin/onboarding', { replace: true });
            return;
          }
        } catch { /* ignore — proceed with overview */ }
        const [c, s] = await Promise.all([
          axios.get(`${API_URL}/api/admin/call/config`, { headers: authHdr() }),
          axios.get(`${API_URL}/api/admin/call/stats`, { headers: authHdr() }),
        ]);
        setCfg(c.data); setStats(s.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Shell current="/admin">
      <div className="p-6 max-w-5xl" data-testid="ptcc-overview">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2"><Phone className="w-6 h-6"/> Overview</h1>
        <p className="text-sm text-slate-500 mb-6">Your dedicated phone number, minute balance and today&apos;s activity.</p>
        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded" data-testid="ptcc-error">{err}</div>}
        {cfg && !cfg.phone_number && (
          <div className="p-3 mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded flex items-center gap-2" data-testid="ptcc-no-did">
            <AlertCircle className="w-4 h-4"/> Your dedicated phone number has not been assigned yet. The platform owner will assign a unique number once your account is fully approved.
          </div>
        )}
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400"/> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Your phone number" value={cfg?.phone_number || '— not assigned —'} testid="ptcc-phone"/>
              <TenantBalanceStat/>
              <StatCard label="Calls today" value={stats?.calls_today ?? 0}/>
              <StatCard label="Status" value={stats?.is_enabled ? 'Active' : 'Disabled'}/>
            </div>
            <WrapUpSetting initial={cfg?.wrap_up_seconds ?? 40}/>
          </>
        )}
      </div>
    </Shell>
  );
}

function WrapUpSetting({ initial }) {
  const [seconds, setSeconds] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setSaving(true); setErr(null); setSaved(false);
    try {
      await axios.patch(`${API_URL}/api/admin/call/config`,
        { wrap_up_seconds: Number(seconds) }, { headers: authHdr() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm max-w-lg" data-testid="ptcc-wrapup-setting">
      <div className="flex items-center gap-2 mb-1">
        <Settings className="w-4 h-4 text-slate-500"/>
        <h2 className="font-medium">Wrap-up timer</h2>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        How long operators pause between calls to finish notes before the auto-redialer fires. 0 seconds disables the pause. Range 0–300.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          max="300"
          value={seconds}
          onChange={e => setSeconds(e.target.value)}
          className="w-24 px-3 py-2 border rounded font-mono text-sm"
          data-testid="ptcc-wrapup-input"
        />
        <span className="text-sm text-slate-500">seconds</span>
        <button onClick={save} disabled={saving} className="ml-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-2 text-sm" data-testid="ptcc-wrapup-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Save
        </button>
        {saved && <span className="text-emerald-600 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Saved</span>}
      </div>
      {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
    </div>
  );
}
function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-lg font-semibold mt-1 text-slate-800" data-testid={testid}>{value}</div>
    </div>
  );
}

function TenantBalanceStat() {
  const [bal, setBal] = useState(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/balance`, { headers: authHdr() })
      .then(r => { setBal(r.data.balance_kr); setBlocked(r.data.blocked); })
      .catch(() => setBal(0));
  }, []);
  const color = bal === null ? 'text-slate-500' : bal <= 0 ? 'text-red-600' : bal < 50 ? 'text-amber-600' : 'text-emerald-700';
  return (
    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
      <div className="text-xs uppercase text-slate-500">Prepaid balance</div>
      <div className={`text-lg font-semibold mt-1 font-mono ${color}`} data-testid="ptcc-tenant-balance">
        {bal === null ? '…' : `${bal.toFixed(2)} kr`}
      </div>
      {blocked && <div className="text-xs text-red-600 mt-1">⚠ Calls blocked</div>}
      {!blocked && bal !== null && bal < 50 && bal > 0 && <div className="text-xs text-amber-600 mt-1">Low — top up soon</div>}
    </div>
  );
}


// -----------------------------------------------------------------------
// ADMIN — Lists
// -----------------------------------------------------------------------
function Lists() {
  const [lists, setLists] = useState([]);
  const [name, setName] = useState('');
  const [numbers, setNumbers] = useState('');
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/call/lists`, { headers: authHdr() });
      setLists(r.data.lists || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const parseCSV = (raw) => {
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const first = lines[0].toLowerCase();
    const startIdx = /(number|phone)/.test(first) && !/^\+?\d/.test(first) ? 1 : 0;
    return lines.slice(startIdx).map(line => {
      const parts = line.split(/[,;]/).map(p => p.replace(/^["']|["']$/g, '').trim());
      return { number: parts[0], name: parts[1] || null, notes: parts[2] || null };
    }).filter(e => e.number);
  };

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setNumbers(ev.target.result || '');
    reader.readAsText(file);
    if (!name.trim()) setName(file.name.replace(/\.(csv|txt|tsv)$/i, ''));
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) readFile(f);
  };
  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) readFile(f);
  };

  const upload = async () => {
    if (!name.trim() || !numbers.trim()) return;
    setSaving(true); setErr(null);
    try {
      const entries = parseCSV(numbers);
      if (entries.length === 0) { setErr('No valid numbers in file/text'); setSaving(false); return; }
      await axios.post(`${API_URL}/api/admin/call/lists`, { name: name.trim(), entries }, { headers: authHdr() });
      setName(''); setNumbers(''); setFileName(null);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };
  return (
    <Shell current="/admin/lists">
      <div className="p-6 max-w-5xl" data-testid="ptcc-lists">
        <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2"><ListChecks className="w-6 h-6"/> Phone lists</h1>
        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
        <div className="bg-white p-4 rounded-lg border shadow-sm mb-6">
          <h2 className="font-medium mb-3 flex items-center gap-2"><Upload className="w-4 h-4"/> Upload new list</h2>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="List name" className="w-full px-3 py-2 border rounded mb-2" data-testid="ptcc-list-name"/>
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center mb-2 transition-colors ${dragging ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}
            data-testid="ptcc-list-dropzone"
          >
            <Upload className={`w-8 h-8 mx-auto mb-2 ${dragging ? 'text-emerald-600' : 'text-slate-400'}`}/>
            <p className="text-sm text-slate-600 mb-1">
              Drag &amp; drop a CSV file here, or
              <label className="text-emerald-600 cursor-pointer underline ml-1">
                browse
                <input type="file" accept=".csv,.txt,.tsv" onChange={onFileInput} className="hidden" data-testid="ptcc-list-file"/>
              </label>
            </p>
            {fileName && <p className="text-xs text-slate-500">File: {fileName}</p>}
            <p className="text-xs text-slate-400 mt-1">Format: one number per line, columns: <code>number[,name[,notes]]</code></p>
          </div>
          <details>
            <summary className="text-xs text-slate-500 cursor-pointer">Or paste manually</summary>
            <textarea
              value={numbers}
              onChange={e => setNumbers(e.target.value)}
              placeholder={"+4791234567,John Doe,Note\n+4792345678"}
              className="w-full h-32 mt-2 px-3 py-2 border rounded font-mono text-sm"
              data-testid="ptcc-list-numbers"
            />
          </details>
          <button
            onClick={upload}
            disabled={saving || !name.trim() || !numbers.trim()}
            className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50 flex items-center gap-2"
            data-testid="ptcc-upload-list"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Create list
          </button>
        </div>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400"/> : (
          <div className="space-y-2" data-testid="ptcc-lists-rows">
            {lists.length === 0 && <div className="text-slate-500 text-sm">No lists yet.</div>}
            {lists.map(l => (
              <div key={l.id} className="bg-white p-3 rounded border shadow-sm flex items-center justify-between">
                <div>
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.completed_entries}/{l.total_entries} completed · {l.is_active ? 'active' : 'inactive'}</div>
                </div>
                <span className="text-xs px-2 py-1 bg-slate-100 rounded">{l.total_entries} numbers</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// ADMIN — Records
// -----------------------------------------------------------------------
function Records() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/records?limit=100`, { headers: authHdr() })
      .then(r => setRows(r.data.records || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Shell current="/admin/records">
      <div className="p-6" data-testid="ptcc-records">
        <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2"><FileText className="w-6 h-6"/> Call log</h1>
        {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                <tr><th className="p-2 text-left">Time</th><th className="p-2 text-left">Operator</th><th className="p-2 text-left">Number</th><th className="p-2 text-left">Disposition</th><th className="p-2 text-right">Duration</th><th className="p-2 text-center">Sale</th></tr>
              </thead>
              <tbody data-testid="ptcc-records-tbody">
                {rows.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-slate-400">No calls yet</td></tr>}
                {rows.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-2">{r.operator_name || '—'}</td>
                    <td className="p-2 font-mono">{r.customer_number}</td>
                    <td className="p-2">{r.disposition || '—'}</td>
                    <td className="p-2 text-right">{fmtMin(r.duration_seconds)}</td>
                    <td className="p-2 text-center">{r.is_sale ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// ADMIN — Leaderboard
// -----------------------------------------------------------------------
function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/leaderboard`, { headers: authHdr() })
      .then(r => setRows(r.data.leaderboard || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Shell current="/admin/leaderboard">
      <div className="p-6" data-testid="ptcc-leaderboard">
        <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2"><Trophy className="w-6 h-6"/> Leaderboard</h1>
        {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                <tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Operator</th><th className="p-2 text-right">Calls</th><th className="p-2 text-right">Answered</th><th className="p-2 text-right">Sales</th><th className="p-2 text-right">Talk time</th><th className="p-2 text-right">Score</th></tr>
              </thead>
              <tbody data-testid="ptcc-leaderboard-tbody">
                {rows.length === 0 && <tr><td colSpan="7" className="p-4 text-center text-slate-400">No data</td></tr>}
                {rows.map((s, i) => (
                  <tr key={s.operator_id} className="border-t">
                    <td className="p-2 font-semibold">{i === 0 ? '🏆' : i + 1}</td>
                    <td className="p-2">{s.operator_name}</td>
                    <td className="p-2 text-right">{s.calls_total || 0}</td>
                    <td className="p-2 text-right">{s.calls_answered || 0}</td>
                    <td className="p-2 text-right font-semibold text-emerald-600">{s.sales_total || 0}</td>
                    <td className="p-2 text-right">{fmtMin(s.seconds_talked)}</td>
                    <td className="p-2 text-right font-bold">{s.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// ADMIN — Team management (operators)
// -----------------------------------------------------------------------
function TeamManagement() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/operators`, { headers: authHdr() });
      setOperators(r.data.operators || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load operators');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (op) => {
    if (!window.confirm(`Delete operator "${op.name || op.email}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/operators/${op.id}`, { headers: authHdr() });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleActive = async (op) => {
    try {
      await axios.patch(`${API_URL}/api/admin/operators/${op.id}`,
        { is_active: !op.is_active }, { headers: authHdr() });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update'); }
  };

  const resetPassword = async (op) => {
    const pw = window.prompt(`New password for ${op.email} (min 6 chars):`);
    if (!pw) return;
    try {
      await axios.patch(`${API_URL}/api/admin/operators/${op.id}`,
        { password: pw }, { headers: authHdr() });
      alert('Password updated');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Shell current="/admin/team">
      <div className="p-6" data-testid="ptcc-team-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="w-6 h-6"/> Operators
          </h1>
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-2"
            data-testid="ptcc-team-add-btn">
            <Users className="w-4 h-4"/> Add operator
          </button>
        </div>

        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}

        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="ptcc-team-table">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Username</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline"/> Loading…</td></tr>
              )}
              {!loading && operators.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">No operators yet. Click &quot;Add operator&quot; to invite your first one.</td></tr>
              )}
              {operators.map(op => (
                <tr key={op.id} className="border-t hover:bg-slate-50" data-testid={`ptcc-op-${op.id}`}>
                  <td className="p-3">{op.name || '—'}</td>
                  <td className="p-3 font-mono text-xs">{op.email}</td>
                  <td className="p-3 text-slate-600">{op.username || '—'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${op.role === 'super_admin' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                      {op.role || 'operator'}
                    </span>
                  </td>
                  <td className="p-3">
                    {op.is_active ? (
                      <span className="text-emerald-600 text-xs font-medium">● Active</span>
                    ) : (
                      <span className="text-slate-400 text-xs">○ Deactivated</span>
                    )}
                  </td>
                  <td className="p-3 text-right space-x-2">
                    <button onClick={() => resetPassword(op)} className="text-xs text-blue-600 hover:underline" data-testid={`ptcc-op-reset-${op.id}`}>Reset pw</button>
                    <button onClick={() => toggleActive(op)} className="text-xs text-slate-700 hover:underline" data-testid={`ptcc-op-toggle-${op.id}`}>
                      {op.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => remove(op)} className="text-xs text-red-600 hover:underline" data-testid={`ptcc-op-del-${op.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showCreate && <CreateOperatorModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }}/>}
    </Shell>
  );
}

function CreateOperatorModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('operator');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/admin/operators`,
        { email: email.trim(), username: username.trim() || undefined, name: name.trim() || undefined, password, role },
        { headers: authHdr() });
      onCreated();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to create operator'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} data-testid="ptcc-op-create-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Add operator</h2>
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Email *</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm" data-testid="ptcc-op-email"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Username (optional)</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm" data-testid="ptcc-op-username"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Display name (optional)</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm" data-testid="ptcc-op-name"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Password * (min 6 chars)</label>
            <input type="text" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="ptcc-op-password"/>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm" data-testid="ptcc-op-role">
              <option value="operator">Operator (makes calls)</option>
              <option value="super_admin">Super admin (full access)</option>
            </select>
          </div>
          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm" data-testid="ptcc-op-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1" data-testid="ptcc-op-create-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Create operator
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// ADMIN — Buy Minutes
// -----------------------------------------------------------------------
function BuyMinutes() {
  const [bundles, setBundles] = useState([]);
  const [buying, setBuying] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/bundles`, { headers: authHdr() })
      .then(r => setBundles(r.data.bundles || []));
  }, []);
  const buy = async (id) => {
    setBuying(id); setErr(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/call/purchase`,
        { bundle_id: id, success_url: window.location.href, cancel_url: window.location.href },
        { headers: authHdr() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { setErr(e.response?.data?.detail || 'Purchase failed'); setBuying(null); }
  };
  return (
    <Shell current="/admin/buy">
      <div className="p-6" data-testid="ptcc-buy">
        <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2"><ShoppingCart className="w-6 h-6"/> Buy minutes</h1>
        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bundles.length === 0 && <div className="col-span-3 text-slate-500 text-sm">No bundles available. The platform owner needs to create bundles.</div>}
          {bundles.map(b => (
            <div key={b.id} className="bg-white rounded-lg border shadow-sm p-4" data-testid={`ptcc-bundle-${b.id}`}>
              <div className="font-medium">{b.name}</div>
              <div className="text-3xl font-bold mt-2">{b.minutes} <span className="text-base font-normal text-slate-500">min</span></div>
              <div className="text-lg mt-1">{b.price} {b.currency}</div>
              <button onClick={() => buy(b.id)} disabled={buying === b.id}
                className="w-full mt-3 py-2 bg-emerald-600 text-white rounded disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`ptcc-buy-${b.id}`}>
                {buying === b.id && <Loader2 className="w-4 h-4 animate-spin"/>} Buy
              </button>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// OPERATOR — Shift (auto-dialer)
// -----------------------------------------------------------------------
const DISPOSITIONS = [
  { key: 'answered',  label: 'Answered',    color: 'bg-emerald-500 hover:bg-emerald-600' },
  { key: 'voicemail', label: 'Voicemail',   color: 'bg-blue-500 hover:bg-blue-600' },
  { key: 'no_answer', label: 'No answer',   color: 'bg-slate-500 hover:bg-slate-600' },
  { key: 'refused',   label: 'Refused',     color: 'bg-amber-500 hover:bg-amber-600' },
  { key: 'wrong_num', label: 'Wrong number',color: 'bg-red-500 hover:bg-red-600' },
];

function Shift() {
  const [entry, setEntry] = useState(null);
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [callStart, setCallStart] = useState(null);
  const [notes, setNotes] = useState('');
  const [isSale, setIsSale] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [stats, setStats] = useState({ calls_today: 0, sales_today: 0 });
  const [autoDial, setAutoDial] = useState(() => localStorage.getItem('ptcc_autodial') !== '0');
  const [countdown, setCountdown] = useState(0); // seconds remaining until auto-dial
  const [wrapUpSeconds, setWrapUpSeconds] = useState(40); // tenant-configured
  const [wrapUpRemaining, setWrapUpRemaining] = useState(0); // active wrap-up seconds
  const autoDialArmed = useRef(false); // only auto-dial when a call has already been placed at least once
  const sip = useSipClient();
  const inCall = sip.status === 'dialing' || sip.status === 'in-call';
  const currentRecordIdRef = useRef(null);
  useEffect(() => { localStorage.setItem('ptcc_autodial', autoDial ? '1' : '0'); }, [autoDial]);

  // Fetch tenant-configured wrap-up window on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/operator/call/config`, { headers: authHdr() })
      .then(r => setWrapUpSeconds(Number(r.data?.wrap_up_seconds ?? 40)))
      .catch(() => {});
  }, []);

  const sendPresence = (status, extra = {}) => {
    axios.post(`${API_URL}/api/operator/call/presence`,
      { status, ...extra }, { headers: authHdr() }).catch(() => {});
  };
  useEffect(() => {
    const map = { registered: 'idle', dialing: 'dialing', 'in-call': 'in-call' };
    if (map[sip.status]) sendPresence(map[sip.status], entry ? { current_number: entry.number, customer_name: entry.name } : {});
  }, [sip.status, entry]);
  useEffect(() => {
    const t = setInterval(() => sendPresence(sip.status === 'in-call' ? 'in-call' : (sip.status === 'dialing' ? 'dialing' : 'idle')), 30000);
    const beforeUnload = () => sendPresence('offline');
    window.addEventListener('beforeunload', beforeUnload);
    return () => { clearInterval(t); window.removeEventListener('beforeunload', beforeUnload); sendPresence('offline'); };
  }, [sip.status]);

  useEffect(() => {
    if (sip.status !== 'in-call' || !callStart) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - callStart) / 1000)), 500);
    return () => clearInterval(t);
  }, [sip.status, callStart]);

  const nextNumber = async () => {
    setLoading(true); setError(null); setEntry(null); setDone(false); setCountdown(0); setWrapUpRemaining(0);
    setNotes(''); setIsSale(false); setElapsed(0);
    try {
      const res = await axios.post(`${API_URL}/api/operator/call/next`, {}, { headers: authHdr() });
      if (res.data.done) setDone(true);
      else setEntry(res.data.entry);
    } catch (e) { setError(e.response?.data?.detail || 'Could not fetch next number'); }
    finally { setLoading(false); }
  };

  const startCall = () => {
    if (!entry?.number) return;
    setError(null);
    setCountdown(0); // any manual click cancels the countdown
    autoDialArmed.current = true;
    if (sip.status === 'disabled') {
      setCallStart(Date.now());
      window.open(`tel:${entry.number}`, '_self');
      return;
    }
    if (sip.status !== 'registered') { setError('SIP not connected. Check SIP config.'); return; }
    setCallStart(Date.now());
    sip.call(entry.number);
  };
  const hangup = () => sip.hangup();
  const toggleMute = () => { const nm = !muted; setMuted(nm); sip.setMuted(nm); };

  // Auto-redialer: when a new entry arrives AND we've already dialed at least once,
  // start a 3s countdown then auto-dial. Cancel on any manual action.
  useEffect(() => {
    if (!entry || !autoDial || !autoDialArmed.current) return;
    if (inCall) return;
    setCountdown(3);
    const t = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(t);
          // fire outside setState using microtask so latest entry is used
          Promise.resolve().then(() => startCall());
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [entry?.id]);

  // If SIP goes dialing/in-call (dialed via any path), stop the countdown UI
  useEffect(() => { if (inCall && countdown > 0) setCountdown(0); }, [inCall, countdown]);

  const cancelAutoDial = () => setCountdown(0);

  const endAndSave = async (disposition) => {
    setLoading(true); setError(null);
    const recordingBlob = await sip.stopRecording();
    try { sip.hangup(); } catch { /* ignore */ }
    const duration = callStart ? Math.floor((Date.now() - callStart) / 1000) : 0;
    try {
      const res = await axios.post(`${API_URL}/api/operator/call/complete`, {
        entry_id: entry.id, disposition, duration_seconds: duration,
        notes: notes.trim() || null,
        is_sale: isSale && disposition === 'answered',
      }, { headers: authHdr() });
      const recordId = res.data.record_id;
      currentRecordIdRef.current = recordId;
      if (recordingBlob && recordId) {
        const fd = new FormData();
        fd.append('file', recordingBlob, `${recordId}.webm`);
        axios.post(`${API_URL}/api/admin/call/records/${recordId}/recording`, fd,
          { headers: { ...authHdr(), 'Content-Type': 'multipart/form-data' } })
          .catch(err => console.warn('Recording upload failed:', err.response?.data || err.message));
      }
      setStats(s => ({ calls_today: s.calls_today + 1, sales_today: s.sales_today + (isSale ? 1 : 0) }));
      setCallStart(null); setMuted(false);
      // Enter wrap-up phase (post-call notes/breather) BEFORE fetching next number
      if (wrapUpSeconds > 0) {
        setWrapUpRemaining(wrapUpSeconds);
        setLoading(false);
      } else {
        await nextNumber();
      }
    } catch (e) { setError(e.response?.data?.detail || 'Could not save call'); setLoading(false); }
  };

  // Wrap-up countdown: tick every second until it hits 0, then fetch next number
  useEffect(() => {
    if (wrapUpRemaining <= 0) return;
    const t = setInterval(() => {
      setWrapUpRemaining(r => {
        if (r <= 1) { clearInterval(t); Promise.resolve().then(() => finishWrapUp()); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [wrapUpRemaining > 0]);

  const finishWrapUp = async () => {
    // Persist any note edits made during wrap-up on the record we just created
    const rid = currentRecordIdRef.current;
    if (rid && notes.trim()) {
      try {
        await axios.patch(`${API_URL}/api/operator/call/records/${rid}/notes`,
          { notes: notes.trim() }, { headers: authHdr() });
      } catch { /* non-fatal — notes may be identical */ }
    }
    currentRecordIdRef.current = null;
    await nextNumber();
  };
  const endWrapUpNow = () => { setWrapUpRemaining(0); finishWrapUp(); };
  const extendWrapUp = () => setWrapUpRemaining(r => Math.min(r + 30, 300));
  const skip = async () => {
    if (!entry) return;
    setLoading(true); setError(null); setCountdown(0);
    try { sip.hangup(); } catch { /* ignore */ }
    try {
      await axios.post(`${API_URL}/api/operator/call/skip`, { entry_id: entry.id }, { headers: authHdr() });
      setCallStart(null); setMuted(false);
      await nextNumber();
    } catch (e) { setError(e.response?.data?.detail || 'Could not skip'); setLoading(false); }
  };

  const sipStatusBadge = ({
    idle:        { c: 'bg-slate-100 text-slate-600',       l: 'SIP not connected' },
    registering: { c: 'bg-amber-100 text-amber-700',       l: 'Connecting to SIP…' },
    registered:  { c: 'bg-emerald-100 text-emerald-700',   l: '● SIP ready' },
    dialing:     { c: 'bg-blue-100 text-blue-700',         l: 'Dialing…' },
    'in-call':   { c: 'bg-emerald-100 text-emerald-800',   l: '● In call' },
    failed:      { c: 'bg-red-100 text-red-700',           l: '✕ SIP error' },
    disabled:    { c: 'bg-slate-100 text-slate-500',       l: 'Using tel: fallback' },
  })[sip.status] || { c: 'bg-slate-100', l: sip.status };

  return (
    <Shell current="/operator">
      <div className="p-6 max-w-3xl" data-testid="ptcc-shift">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2"><PhoneCall className="w-6 h-6"/> Operator shift</h1>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer select-none" data-testid="ptcc-autodial-toggle">
              <input type="checkbox" checked={autoDial} onChange={e => { setAutoDial(e.target.checked); if (!e.target.checked) setCountdown(0); }}/>
              Auto-dial (3s)
            </label>
            <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${sipStatusBadge.c}`} data-testid="ptcc-sip-status">
              <Radio className="w-3 h-3"/> {sipStatusBadge.l}
            </span>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-6">Click &quot;Start shift&quot; to fetch the next number from the active list.</p>
        {sip.error && <div className="p-3 mb-3 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">{sip.error}</div>}
        {error && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded" data-testid="ptcc-shift-error">{error}</div>}

        <audio ref={sip.audioRef} autoPlay data-testid="ptcc-audio"/>

        {wrapUpRemaining > 0 && (
          <div className="bg-white rounded-lg border-2 border-amber-400 shadow-sm p-6 mb-4" data-testid="ptcc-wrapup">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-amber-700 uppercase font-semibold flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"/> Wrap-up
              </span>
              <span className="text-xs text-slate-500">{stats.calls_today} calls today</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-20 h-20 rounded-full bg-amber-100 border-4 border-amber-400 text-amber-800 flex items-center justify-center text-3xl font-bold" data-testid="ptcc-wrapup-remaining">
                {wrapUpRemaining}
              </div>
              <div>
                <div className="font-semibold text-slate-800">Take a breath. Update your notes.</div>
                <div className="text-sm text-slate-500">Next call will start in {wrapUpRemaining} second{wrapUpRemaining === 1 ? '' : 's'}.</div>
              </div>
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Post-call notes (saved with the call record)…"
              className="w-full h-20 px-3 py-2 border rounded resize-none"
              data-testid="ptcc-wrapup-notes"
            />
            <div className="flex items-center gap-2 mt-3">
              <button onClick={extendWrapUp} className="px-3 py-2 border rounded text-sm hover:bg-slate-50" data-testid="ptcc-wrapup-extend">
                +30s
              </button>
              <button onClick={endWrapUpNow} className="ml-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm flex items-center gap-2" data-testid="ptcc-wrapup-end">
                <SkipForward className="w-4 h-4"/> End wrap-up now
              </button>
            </div>
          </div>
        )}

        {!entry && !done && wrapUpRemaining === 0 && (
          <div className="bg-white p-8 rounded-lg border shadow-sm text-center">
            <div className="text-6xl mb-3">📞</div>
            <div className="text-lg mb-4">Ready to start your shift?</div>
            <button onClick={nextNumber} disabled={loading}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 mx-auto disabled:opacity-50"
              data-testid="ptcc-start-shift">
              {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <PlayCircle className="w-5 h-5"/>}
              Start shift
            </button>
          </div>
        )}

        {done && (
          <div className="bg-white p-8 rounded-lg border shadow-sm text-center" data-testid="ptcc-shift-done">
            <div className="text-6xl mb-3">🎉</div>
            <div className="text-lg mb-2">All numbers in the list are complete!</div>
            <div className="text-sm text-slate-500 mb-4">Today: {stats.calls_today} calls · {stats.sales_today} sales</div>
            <button onClick={nextNumber} className="px-4 py-2 border rounded">Check for a new list</button>
          </div>
        )}

        {entry && (
          <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="ptcc-active-entry">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase">Current call</span>
              <span className="text-xs px-2 py-1 bg-slate-100 rounded">{stats.calls_today} today</span>
            </div>
            <div className="p-4 rounded bg-slate-50 border border-slate-200 mb-4">
              <div className="text-3xl font-mono font-bold text-slate-800" data-testid="ptcc-active-number">{entry.number}</div>
              {entry.name && <div className="text-slate-600 mt-1">{entry.name}</div>}
              {entry.notes && <div className="text-xs text-slate-500 mt-2 italic">{entry.notes}</div>}
            </div>
            {inCall ? (
              <div className="p-4 rounded bg-emerald-50 border border-emerald-200 flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"/>
                  {sip.status === 'dialing' ? 'Dialing…' : 'In call'}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={toggleMute} className={`p-2 rounded-full ${muted ? 'bg-red-100 text-red-600' : 'bg-white border text-slate-600'}`} data-testid="ptcc-mute">
                    {muted ? <MicOff className="w-4 h-4"/> : <Mic className="w-4 h-4"/>}
                  </button>
                  <div className="text-2xl font-mono font-bold text-emerald-800" data-testid="ptcc-timer">{fmtMSS(elapsed)}</div>
                  <button onClick={hangup} className="p-2 rounded-full bg-red-500 text-white" data-testid="ptcc-hangup"><PhoneOff className="w-4 h-4"/></button>
                </div>
              </div>
            ) : countdown > 0 ? (
              <div className="w-full mb-4 p-4 rounded-lg bg-emerald-50 border-2 border-emerald-500" data-testid="ptcc-countdown">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center text-2xl font-bold" data-testid="ptcc-countdown-number">
                      {countdown}
                    </div>
                    <div>
                      <div className="font-semibold text-emerald-800">Auto-dialing in {countdown}s…</div>
                      <div className="text-xs text-emerald-700">Calling {entry.number}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={startCall} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm flex items-center gap-1" data-testid="ptcc-dial-now">
                      <Phone className="w-4 h-4"/> Dial now
                    </button>
                    <button onClick={cancelAutoDial} className="px-3 py-2 border border-emerald-500 text-emerald-700 hover:bg-emerald-100 rounded text-sm" data-testid="ptcc-cancel-autodial">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={startCall} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center justify-center gap-2 mb-4" data-testid="ptcc-dial">
                <Phone className="w-5 h-5"/> Call {entry.number}
              </button>
            )}
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note…" className="w-full px-3 py-2 border rounded mb-3" data-testid="ptcc-notes"/>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input type="checkbox" checked={isSale} onChange={e => setIsSale(e.target.checked)} data-testid="ptcc-is-sale"/> Sale registered ✓
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              {DISPOSITIONS.map(d => (
                <button key={d.key} onClick={() => endAndSave(d.key)} disabled={loading}
                  className={`text-white py-2 rounded ${d.color} disabled:opacity-50`}
                  data-testid={`ptcc-disp-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-3 border-t">
              <button onClick={skip} disabled={loading} className="flex-1 px-3 py-2 border rounded flex items-center justify-center gap-2" data-testid="ptcc-skip">
                <SkipForward className="w-4 h-4"/> Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// TEAM CHAT — WebSocket-based, tenant-scoped
// -----------------------------------------------------------------------
function TeamChat() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [err, setErr] = useState(null);
  const wsRef = useRef(null);
  const meId = (() => {
    try { const t = getToken(); if (!t) return null;
      const payload = JSON.parse(atob(t.split('.')[1] || '')); return payload.sub || payload.id || null;
    } catch { return null; }
  })();

  useEffect(() => {
    axios.get(`${API_URL}/api/callcenter/chat/messages?limit=200`, { headers: authHdr() })
      .then(r => setMessages(r.data.messages || []))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let closed = false;
    let retryTimer = null;
    const connect = () => {
      if (closed) return;
      const tok = getToken();
      if (!tok) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/callcenter/chat?token=${encodeURIComponent(tok)}`);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); setErr(null); };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'message') {
            setMessages(m => (m.some(x => x.id === data.id) ? m : [...m, data]));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retryTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; if (retryTimer) clearTimeout(retryTimer); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  const send = (e) => {
    e?.preventDefault();
    const content = text.trim();
    if (!content) return;
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ content }));
      setText('');
    } else {
      axios.post(`${API_URL}/api/callcenter/chat/send`, { content }, { headers: authHdr() })
        .then(r => { setMessages(m => [...m, r.data]); setText(''); })
        .catch(err => setErr(err.response?.data?.detail || 'Could not send'));
    }
  };

  return (
    <Shell current="/team-chat">
      <div className="p-6 max-w-3xl h-full flex flex-col" data-testid="ptcc-team-chat">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2"><MessageCircle className="w-6 h-6"/> Team chat</h1>
          <span className={`text-xs px-2 py-0.5 rounded ${connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-testid="ptcc-chat-status">
            {connected ? '● Live' : '○ Connecting…'}
          </span>
        </div>
        <p className="text-sm text-slate-500 mb-4">Only for your company. Operators + admins can talk here.</p>
        {err && <div className="p-3 mb-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}
        <div className="flex-1 min-h-[400px] bg-white rounded-lg border shadow-sm p-4 overflow-y-auto space-y-2 mb-3" data-testid="ptcc-chat-log">
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400"/> : null}
          {!loading && messages.length === 0 && (
            <div className="text-slate-400 text-sm text-center py-8">No messages yet. Send the first one!</div>
          )}
          {messages.map(m => {
            const mine = meId && m.sender_id === meId;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                  <div className={`text-xs mb-0.5 opacity-80 ${mine ? 'text-emerald-100' : 'text-slate-500'}`}>
                    {m.sender_name} {m.sender_role === 'super_admin' ? '· Admin' : ''} · {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="flex gap-2" data-testid="ptcc-chat-form">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 px-3 py-2 border rounded-lg"
            data-testid="ptcc-chat-input"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="ptcc-chat-send"
          >
            <Send className="w-4 h-4"/> Send
          </button>
        </form>
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// ADMIN — SIP CONFIG
// -----------------------------------------------------------------------
function SipConfig() {
  const [mode, setMode] = useState('pool'); // 'pool' | 'bridge' | 'advanced'
  const [cfg, setCfg] = useState({
    provider: '', sip_server: '', username: '', password: '', from_display: '',
    sip_uri: '', ws_url: '',
  });
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(null);

  // Provider catalog — used to auto-derive sip_uri host and ws_url in simple mode.
  const PROVIDERS = {
    zadarma:  { name: 'Zadarma',  host: 'sip.zadarma.com',        ws: 'wss://webrtc.zadarma.com:8443/webrtc' },
    twilio:   { name: 'Twilio',   host: '{DOMAIN}.sip.twilio.com', ws: 'wss://{DOMAIN}.sip.twilio.com:5061', needsDomain: true },
    telnyx:   { name: 'Telnyx',   host: 'sip.telnyx.com',         ws: 'wss://sip.telnyx.com:443' },
    plivo:    { name: 'Plivo',    host: 'sip.plivo.com',          ws: 'wss://phone.plivo.com/webrtc' },
    elks:     { name: '46elks',   host: 'sip.46elks.com',         ws: 'wss://sip.46elks.com/w1/websocket' },
    signalwire:{ name: 'SignalWire', host: '{SPACE}.sip.signalwire.com', ws: 'wss://{SPACE}.sip.signalwire.com:443', needsDomain: true },
    other:    { name: 'Other / custom', host: '', ws: '' },
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/sip-config`, { headers: authHdr() })
      .then(r => {
        setExisting(r.data);
        if (r.data.configured) {
          setCfg({
            provider: r.data.provider || '',
            sip_server: r.data.sip_server || (r.data.sip_uri || '').split('@')[1] || '',
            username: r.data.username || '',
            password: r.data.password || '',
            from_display: r.data.from_display || '',
            sip_uri: r.data.sip_uri || '',
            ws_url: r.data.ws_url || '',
          });
        }
      });
  }, []);

  const selectProvider = (key) => {
    const p = PROVIDERS[key];
    setCfg(c => ({
      ...c,
      provider: key,
      sip_server: p.needsDomain ? '' : p.host,
      ws_url: p.needsDomain ? '' : p.ws,
    }));
  };

  // Build the fully-qualified sip_uri from the simple fields
  const derivedSipUri = () => {
    if (!cfg.username || !cfg.sip_server) return '';
    return `sip:${cfg.username}@${cfg.sip_server}`;
  };

  // Validators
  const wsValid = !cfg.ws_url || /^wss:\/\/[^\s]+/.test(cfg.ws_url.trim());
  const simpleValid = cfg.sip_server && cfg.username && cfg.password && cfg.ws_url && wsValid;
  const advValid = cfg.sip_uri && /^sip:[^@\s]+@[^\s]+$/.test(cfg.sip_uri.trim())
                   && cfg.ws_url && wsValid && cfg.username && cfg.password;
  const canSubmit = mode === 'simple' ? simpleValid : advValid;

  const save = async (e) => {
    e.preventDefault();
    if (mode !== 'bridge' && !canSubmit) { setErr('Please fill all required fields correctly.'); return; }
    if (mode === 'bridge' && !(cfg.sip_server && cfg.username && cfg.password)) {
      setErr('SIP server, username and password are required for Bridge Mode.'); return;
    }
    setSaving(true); setErr(null); setOk(false);
    if (mode === 'bridge') {
      try {
        const r = await axios.post(`${API_URL}/api/admin/call/bridge-config`, {
          sip_server: cfg.sip_server.trim(),
          username: cfg.username.trim(),
          password: cfg.password,
          from_display: cfg.from_display,
          provider: cfg.provider,
        }, { headers: authHdr() });
        setOk(true);
        setExisting({ ...cfg, configured: true, bridge_mode: true });
        if (!r.data.asterisk_reload_ok) {
          setErr(`Saved, but Asterisk reload had issues: ${r.data.asterisk_reload_msg || 'unknown'}. Contact ops.`);
        }
      } catch (ex) { setErr(ex.response?.data?.detail || 'Save failed'); }
      finally { setSaving(false); }
      return;
    }
    const payload = mode === 'simple' ? {
      sip_uri: derivedSipUri(),
      ws_url: cfg.ws_url.trim(),
      username: cfg.username,
      password: cfg.password,
      from_display: cfg.from_display,
      sip_server: cfg.sip_server,
      provider: cfg.provider,
    } : {
      sip_uri: cfg.sip_uri.trim(),
      ws_url: cfg.ws_url.trim(),
      username: cfg.username,
      password: cfg.password,
      from_display: cfg.from_display,
    };
    try {
      await axios.post(`${API_URL}/api/admin/call/sip-config`, payload, { headers: authHdr() });
      setOk(true); setExisting({ ...payload, configured: true });
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm('Delete SIP config? Operators will no longer be able to make calls.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/call/sip-config`, { headers: authHdr() });
      setExisting({ configured: false });
      setCfg({ provider: '', sip_server: '', username: '', password: '', from_display: '', sip_uri: '', ws_url: '' });
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Shell current="/admin/sip">
      <div className="p-6 max-w-2xl" data-testid="ptcc-sip">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2"><Settings className="w-6 h-6"/> Voice / SIP setup</h1>
        <p className="text-sm text-slate-500 mb-4">
          Connect your SIP provider so your operators can dial from the browser.
          Only takes a few fields — pick your provider below.
        </p>

        {/* Mode toggle */}
        <div className="mb-4 inline-flex rounded-lg border border-slate-300 overflow-hidden" data-testid="sip-mode">
          <button type="button" onClick={() => setMode('pool')}
            className={`px-4 py-1.5 text-sm ${mode === 'pool' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700'}`}
            data-testid="sip-mode-pool">📱 Priority Talk number</button>
          <button type="button" onClick={() => setMode('bridge')}
            className={`px-4 py-1.5 text-sm ${mode === 'bridge' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700'}`}
            data-testid="sip-mode-bridge">🔗 My own carrier</button>
          <button type="button" onClick={() => setMode('advanced')}
            className={`px-4 py-1.5 text-sm ${mode === 'advanced' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700'}`}
            data-testid="sip-mode-advanced">Advanced</button>
        </div>

        {mode === 'pool' && (
          <div className="mb-3 bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-900">
            <b>📱 Priority Talk number:</b> Pick a phone number from Priority Talk&apos;s pool. No SIP setup, no third-party carrier — just click and call.
          </div>
        )}
        {mode === 'bridge' && (
          <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-900">
            <b>🔗 My own carrier:</b> Bring your own SIP provider credentials (Telnyx, Zadarma, etc.). Asterisk on this VPS bridges the connection.
          </div>
        )}

        {ok && <div className="p-3 mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/> Saved successfully — operators can now make calls.</div>}
        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded flex items-center gap-2"><AlertCircle className="w-4 h-4"/> {err}</div>}

        <form onSubmit={save} className="bg-white p-5 rounded-lg border shadow-sm space-y-4">
          {mode === 'pool' ? (
            <PoolPickerInline/>
          ) : mode === 'bridge' ? (
            <>
              <SipField label="SIP server (from your provider — just the hostname)" placeholder="sip.zadarma.com"
                value={cfg.sip_server} onChange={v => setCfg({...cfg, sip_server: v})} testid="bridge-server"/>
              <SipField label="Username (SIP-account ID from provider, e.g. 100)" placeholder="100"
                value={cfg.username} onChange={v => setCfg({...cfg, username: v})} testid="bridge-username"/>
              <SipField label="Password" type="password" value={cfg.password}
                onChange={v => setCfg({...cfg, password: v})} testid="bridge-password"/>
              <SipField label="Display number for outgoing calls (E.164)" placeholder="+4712345678"
                value={cfg.from_display} onChange={v => setCfg({...cfg, from_display: v})} testid="bridge-display"/>
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Asterisk will register as <b>sip:{cfg.username || 'USER'}@{cfg.sip_server || 'sip.provider.com'}</b> and forward operator calls out through this trunk.
              </div>
            </>
          ) : mode === 'simple' ? (
            <>
              <div>
                <label className="block text-xs uppercase text-slate-500 mb-1">Provider</label>
                <select value={cfg.provider} onChange={e => selectProvider(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm bg-white" data-testid="sip-provider">
                  <option value="">— Choose your provider —</option>
                  {Object.entries(PROVIDERS).map(([key, p]) => (
                    <option key={key} value={key}>{p.name}</option>
                  ))}
                </select>
              </div>
              {cfg.provider && (
                <>
                  <SipField label="SIP server (host)" placeholder="sip.provider.com"
                    value={cfg.sip_server} onChange={v => setCfg({...cfg, sip_server: v})} testid="sip-server"/>
                  <SipField label="Username (from your provider)" placeholder="100 or your account username"
                    value={cfg.username} onChange={v => setCfg({...cfg, username: v})} testid="sip-username"/>
                  <SipField label="Password" type="password" value={cfg.password}
                    onChange={v => setCfg({...cfg, password: v})} testid="sip-password"/>
                  <SipField label="WebSocket URL (from provider's WebRTC settings)"
                    placeholder="wss://sip.provider.com:443"
                    value={cfg.ws_url} onChange={v => setCfg({...cfg, ws_url: v})} testid="sip-ws"
                    error={!wsValid && 'Must start with wss://'}/>
                  <SipField label="Display number to show on outgoing calls (E.164)" placeholder="+4712345678"
                    value={cfg.from_display} onChange={v => setCfg({...cfg, from_display: v})} testid="sip-display"/>

                  {cfg.username && cfg.sip_server && (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 font-mono" data-testid="sip-derived-uri">
                      Auto-derived SIP URI: <b>{derivedSipUri()}</b>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <SipField label="SIP URI" placeholder="sip:username@sip.provider.com"
                value={cfg.sip_uri} onChange={v => setCfg({...cfg, sip_uri: v})} testid="sip-uri"
                error={cfg.sip_uri && !/^sip:[^@\s]+@[^\s]+$/.test(cfg.sip_uri.trim()) && 'Must be sip:user@host format'}/>
              <SipField label="WebSocket URL" placeholder="wss://sip.provider.com:7443"
                value={cfg.ws_url} onChange={v => setCfg({...cfg, ws_url: v})} testid="sip-ws"
                error={!wsValid && 'Must start with wss://'}/>
              <SipField label="Username" value={cfg.username} onChange={v => setCfg({...cfg, username: v})} testid="sip-username"/>
              <SipField label="Password" type="password" value={cfg.password} onChange={v => setCfg({...cfg, password: v})} testid="sip-password"/>
              <SipField label="Display number (E.164)" placeholder="+4712345678"
                value={cfg.from_display} onChange={v => setCfg({...cfg, from_display: v})} testid="sip-display"/>
            </>
          )}

          <div className="flex items-center gap-2 pt-2">
            {mode !== 'pool' && (
              <button type="submit" disabled={saving || (mode !== 'bridge' && !canSubmit)}
                className="px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50 flex items-center gap-2"
                data-testid="sip-save">
                {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Save &amp; connect
              </button>
            )}
            {existing?.configured && mode !== 'pool' && (
              <button type="button" onClick={del} className="px-4 py-2 border rounded text-red-600" data-testid="sip-delete">Delete config</button>
            )}
            {existing?.configured && (
              <SipTestButton/>
            )}
          </div>
        </form>

        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-slate-600">Where do I find the WebSocket URL?</summary>
          <div className="mt-2 text-slate-600 space-y-2">
            <p>Most modern SIP providers offer a WebRTC / SIP-over-WebSocket endpoint. Look in your provider&apos;s dashboard under one of these labels:</p>
            <ul className="list-disc pl-6">
              <li>WebRTC / Cloud Softphone</li>
              <li>SIP over WebSocket (WSS)</li>
              <li>Browser calling / JsSIP endpoint</li>
            </ul>
            <p className="mt-2"><b>Twilio:</b> Voice → SIP Domains → Your domain → Voice Configuration → WSS URL is <code>wss://YOURDOMAIN.sip.twilio.com:5061</code></p>
            <p><b>Telnyx:</b> Voice → SIP Connections → Your connection → WebRTC tab</p>
            <p><b>Zadarma:</b> Settings → API → WebRTC → <code>wss://webrtc.zadarma.com:8443/webrtc</code></p>
            <p className="mt-2 text-amber-700">If your provider does <i>not</i> offer WSS, contact us — we can install a local Asterisk WSS bridge on your VPS in a follow-up release.</p>
          </div>
        </details>
      </div>
    </Shell>
  );
}
function SipField({ label, value, onChange, placeholder, type = 'text', testid, error }) {
  return (
    <div>
      <label className="block text-xs uppercase text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border rounded font-mono text-sm ${error ? 'border-red-400 bg-red-50' : ''}`}
        data-testid={`ptcc-${testid}`}
      />
      {error && <p className="mt-1 text-xs text-red-600" data-testid={`ptcc-${testid}-error`}>{error}</p>}
    </div>
  );
}

// Quick live SIP test — opens a temporary JsSIP UA using the operator SIP-credentials
// endpoint (which reflects the saved config) and reports what actually happens.
function SipTestButton() {
  const [state, setState] = useState('idle'); // idle | testing | ok | fail
  const [msg, setMsg] = useState('');

  const runTest = async () => {
    setState('testing'); setMsg('Running diagnostics…');
    let ua = null;
    let timer = null;
    const done = (ok, m) => {
      setState(ok ? 'ok' : 'fail'); setMsg(m);
      if (timer) clearTimeout(timer);
      try { ua?.stop(); } catch { /* ignore */ }
    };
    try {
      // Step 1: server-side diagnostics first — gives specific hints
      try {
        const diag = await axios.get(`${API_URL}/api/admin/call/bridge-status`, { headers: authHdr() });
        const failed = (diag.data.checks || []).find(c => !c.ok);
        if (failed) {
          done(false, `${failed.step}: ${failed.hint}`);
          return;
        }
      } catch (e) {
        // Diagnostic endpoint not available on old installs; fall through to WSS test
      }
      // Step 2: live SIP REGISTER test
      const r = await axios.get(`${API_URL}/api/operator/call/sip-credentials`, { headers: authHdr() });
      const c = r.data;
      setMsg('Connecting to WebSocket…');
      let wsUrl = c.ws_url;
      if (wsUrl && wsUrl.startsWith('/')) {
        const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${scheme}//${window.location.host}${wsUrl}`;
      }
      const socket = new JsSIP.WebSocketInterface(wsUrl);
      ua = new JsSIP.UA({
        sockets: [socket], uri: c.sip_uri, password: c.password,
        display_name: c.from_display || undefined, register: true,
      });
      timer = setTimeout(() => done(false, `Timed out after 12s. ${ua.isConnected()
          ? 'WebSocket connected but SIP REGISTER did not complete — check username/password/URI.'
          : `Could not reach ${c.ws_url}. Verify the WSS URL and browser network.`}`), 12000);
      ua.on('connected',    () => setMsg('WebSocket connected. Registering…'));
      ua.on('disconnected', (e) => done(false, `WebSocket failed: ${e?.reason || 'connection closed'} (code ${e?.code || '?'})`));
      ua.on('registered',   () => done(true, `SIP registered as ${c.sip_uri}. Operators are ready to make calls.`));
      ua.on('registrationFailed', (e) => {
        const cause = e?.cause || 'unknown';
        const status = e?.response?.status_code;
        done(false, `REGISTER failed: ${cause}${status ? ` (${status})` : ''}`);
      });
      ua.start();
    } catch (e) {
      done(false, e.response?.data?.detail || String(e));
    }
  };

  return (
    <button type="button" onClick={runTest} disabled={state === 'testing'}
      className={`px-4 py-2 border rounded text-sm flex items-center gap-2 ${
        state === 'ok' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
        state === 'fail' ? 'bg-red-50 border-red-300 text-red-700' :
        'text-slate-700 hover:bg-slate-50'
      }`}
      data-testid="sip-test-btn"
      title={msg}>
      {state === 'testing' && <Loader2 className="w-4 h-4 animate-spin"/>}
      {state === 'ok' && <CheckCircle2 className="w-4 h-4"/>}
      {state === 'fail' && <AlertCircle className="w-4 h-4"/>}
      {state === 'idle' ? 'Test connection' : state === 'testing' ? 'Testing…' : state === 'ok' ? 'Connected ✓' : 'Failed'}
      {msg && state !== 'idle' && state !== 'testing' && <span className="ml-2 text-xs font-normal opacity-80 max-w-md truncate" data-testid="sip-test-msg">— {msg}</span>}
    </button>
  );
}

// -----------------------------------------------------------------------
// Pool picker — tenant chooses a DID from master's reseller pool
// -----------------------------------------------------------------------
function PoolPickerInline() {
  const [state, setState] = useState({ available: [], my_number: null, loading: true, err: null });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setState(s => ({ ...s, loading: true, err: null }));
    try {
      const r = await axios.get(`${API_URL}/api/admin/call/available-numbers`, { headers: authHdr() });
      setState({ available: r.data.available || [], my_number: r.data.my_number, loading: false, err: null });
    } catch (e) {
      setState({ available: [], my_number: null, loading: false, err: e.response?.data?.detail || 'Failed to load' });
    }
  };
  useEffect(() => { load(); }, []);

  const claim = async (did) => {
    if (!window.confirm(`Claim ${did.number} at ${Number(did.price_retail || 0).toFixed(2)} / mo?`)) return;
    setBusy(did.id);
    try {
      await axios.post(`${API_URL}/api/admin/call/claim-number/${did.id}`, {}, { headers: authHdr() });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Claim failed'); }
    finally { setBusy(null); }
  };
  const release = async () => {
    if (!window.confirm('Release your current number back to the pool?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/call/release-number`, {}, { headers: authHdr() });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Release failed'); }
  };

  if (state.loading) return <div className="p-4 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline"/> Loading numbers…</div>;
  if (state.err) return <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded">{state.err}</div>;

  return (
    <div data-testid="pool-picker">
      {state.my_number && (
        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded" data-testid="pool-current">
          <div className="text-xs text-purple-700 uppercase mb-1">Your active number</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-semibold font-mono">{state.my_number.number}</div>
              <div className="text-xs text-slate-600 mt-1">{Number(state.my_number.price_retail || 0).toFixed(2)} / month · assigned {fmtDate(state.my_number.assigned_at)}</div>
            </div>
            <button onClick={release} className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50" data-testid="pool-release">Release number</button>
          </div>
        </div>
      )}
      <h3 className="text-sm font-semibold text-slate-700 mb-2">
        {state.my_number ? 'Or switch to a different number:' : 'Choose a number for your company:'}
      </h3>
      {state.available.length === 0 ? (
        <div className="p-6 text-center text-slate-500 bg-white border border-dashed rounded">
          No numbers currently available. Please contact your administrator.
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700"><tr>
              <th className="text-left p-3">Number</th>
              <th className="text-right p-3">Price / month</th>
              <th className="text-right p-3"></th>
            </tr></thead>
            <tbody>
              {state.available.map(d => (
                <tr key={d.id} className="border-t hover:bg-slate-50" data-testid={`pool-did-${d.number}`}>
                  <td className="p-3 font-mono text-base">{d.number}</td>
                  <td className="p-3 text-right font-mono">{Number(d.price_retail || 0).toFixed(2)}</td>
                  <td className="p-3 text-right">
                    <button type="button" onClick={() => claim(d)} disabled={busy === d.id}
                      className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50 flex items-center gap-1 ml-auto"
                      data-testid={`pool-claim-${d.number}`}>
                      {busy === d.id ? <Loader2 className="w-3 h-3 animate-spin"/> : null}
                      Claim number
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------
// SIP Client hook — in-house WebRTC dialer + MediaRecorder for call recording
// -----------------------------------------------------------------------
function useSipClient() {
  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const localStreamRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    axios.get(`${API_URL}/api/operator/call/sip-credentials`, { headers: authHdr() }).then(r => {
      if (!mounted) return;
      const c = r.data;
      setCfg(c);
      try {
        // Resolve relative WSS URLs (Bridge Mode returns "/asterisk-ws")
        let wsUrl = c.ws_url;
        if (wsUrl && wsUrl.startsWith('/')) {
          const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          wsUrl = `${scheme}//${window.location.host}${wsUrl}`;
        }
        const socket = new JsSIP.WebSocketInterface(wsUrl);
        const ua = new JsSIP.UA({
          sockets: [socket],
          uri: c.sip_uri,
          password: c.password,
          display_name: c.from_display || undefined,
          register: true,
        });
        setStatus('registering');
        setError(null);
        // Watchdog: if we're not registered within 15 seconds, surface details
        timeoutId = setTimeout(() => {
          if (mounted) {
            const details = ua.isConnected() ? 'WebSocket connected but SIP REGISTER did not complete — check username, password, and SIP URI.'
                                             : `Could not reach ${c.ws_url}. Check that the WebSocket URL is correct and reachable from your browser.`;
            setStatus('failed');
            setError('Timed out after 15s. ' + details);
          }
        }, 15000);
        ua.on('connecting',   () => { setStatus('registering'); setError(null); });
        ua.on('connected',    () => { setStatus('registering'); });
        ua.on('disconnected', (e) => {
          setStatus('failed');
          const reason = e?.error ? `${e.reason || 'WebSocket error'} (code ${e.code || '?'})`
                                  : (e?.reason || 'WebSocket disconnected');
          setError(`WebSocket problem: ${reason}. Verify the WSS URL and that your browser can reach it (open ${c.ws_url} directly in a new tab — it should stay open).`);
        });
        ua.on('registered',   () => { if (timeoutId) clearTimeout(timeoutId); setStatus('registered'); setError(null); });
        ua.on('unregistered', () => setStatus('idle'));
        ua.on('registrationFailed', (e) => {
          if (timeoutId) clearTimeout(timeoutId);
          setStatus('failed');
          const cause = e?.cause || 'Unknown';
          const status = e?.response?.status_code;
          let hint = '';
          if (status === 401 || status === 403 || /Auth/i.test(cause)) hint = ' — wrong username or password.';
          else if (status === 404) hint = ' — SIP URI user not found on the server (double-check the username and domain).';
          else if (/Connection|Timeout/i.test(cause)) hint = ' — provider unreachable.';
          setError(`SIP REGISTER failed: ${cause}${status ? ` (${status})` : ''}${hint}`);
        });
        ua.start();
        uaRef.current = ua;
      } catch (e) {
        setStatus('failed'); setError(String(e));
      }
    }).catch((e) => {
      if (e.response?.status === 404) setStatus('disabled');
      else { setStatus('failed'); setError(e.response?.data?.detail || 'Could not fetch SIP credentials'); }
    });
    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      try { uaRef.current?.stop(); } catch { /* ignore */ }
    };
  }, []);

  const startRecording = (remoteStream) => {
    try {
      if (!localStreamRef.current) return;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      const localSrc = ctx.createMediaStreamSource(localStreamRef.current);
      localSrc.connect(dest);
      if (remoteStream) {
        try { ctx.createMediaStreamSource(remoteStream).connect(dest); } catch { /* ignore */ }
      }
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mr = new MediaRecorder(dest.stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(1000);
      recorderRef.current = { mr, ctx };
    } catch (e) { console.warn('MediaRecorder start failed:', e); }
  };
  const stopRecording = () => new Promise((resolve) => {
    const rec = recorderRef.current;
    if (!rec) return resolve(null);
    rec.mr.onstop = () => {
      try { rec.ctx.close(); } catch { /* ignore */ }
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      chunksRef.current = [];
      recorderRef.current = null;
      resolve(blob.size > 0 ? blob : null);
    };
    try { rec.mr.stop(); } catch { resolve(null); }
  });

  const call = (number, { onEnd, onFail } = {}) => {
    if (!uaRef.current || status !== 'registered') {
      setError('SIP not connected. Check SIP config.'); return null;
    }
    setError(null); setStatus('dialing');
    const audio = audioRef.current;
    const eventHandlers = {
      progress:  () => setStatus('dialing'),
      confirmed: () => setStatus('in-call'),
      failed:    (e) => { setStatus('registered'); setError(e?.cause || 'Call failed'); onFail?.(e); },
      ended:     () => { setStatus('registered'); onEnd?.(); },
      addstream: (e) => {
        if (audio) audio.srcObject = e.stream;
        startRecording(e.stream);
      },
    };
    const opts = {
      eventHandlers,
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: (cfg?.stun_servers || ['stun:stun.l.google.com:19302']).map(u => ({ urls: u })) },
    };
    const session = uaRef.current.call(number, opts);
    if (session?.connection) {
      session.connection.ontrack = (e) => {
        if (audio && e.streams[0]) {
          audio.srcObject = e.streams[0];
          startRecording(e.streams[0]);
        }
      };
    }
    if (session) {
      session.on('sending', () => {
        try {
          const senders = session.connection?.getSenders?.() || [];
          const audioSender = senders.find(s => s.track?.kind === 'audio');
          if (audioSender?.track) {
            localStreamRef.current = new MediaStream([audioSender.track]);
          }
        } catch { /* ignore */ }
      });
    }
    sessionRef.current = session;
    return session;
  };
  const hangup = () => { try { sessionRef.current?.terminate(); } catch { /* ignore */ } };
  const setMuted = (m) => { try { if (m) sessionRef.current?.mute({ audio: true }); else sessionRef.current?.unmute({ audio: true }); } catch { /* ignore */ } };

  return { status, error, call, hangup, setMuted, audioRef, stopRecording };
}

// -----------------------------------------------------------------------
// ADMIN — LIVE MONITOR
// -----------------------------------------------------------------------
function LiveMonitor() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const load = () => {
    axios.get(`${API_URL}/api/admin/call/presence`, { headers: authHdr() })
      .then(r => setRows(r.data.operators || []))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load'));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  const statusMeta = {
    'in-call':  { c: 'bg-emerald-500', l: 'In call',    ring: 'ring-emerald-300' },
    'dialing':  { c: 'bg-blue-500',    l: 'Dialing',    ring: 'ring-blue-300' },
    'idle':     { c: 'bg-slate-400',   l: 'Idle',       ring: 'ring-slate-200' },
    'offline':  { c: 'bg-slate-300',   l: 'Offline',    ring: 'ring-slate-100' },
  };
  const summary = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  return (
    <Shell current="/admin/monitor">
      <div className="p-6" data-testid="ptcc-monitor">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2"><Radio className="w-6 h-6"/> Live monitor</h1>
        <p className="text-sm text-slate-500 mb-4">Refreshes every 3 seconds. Offline = no heartbeat for 2 minutes.</p>
        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {['in-call', 'dialing', 'idle', 'offline'].map(s => (
            <div key={s} className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="text-xs uppercase text-slate-500">{statusMeta[s].l}</div>
              <div className="text-2xl font-bold" data-testid={`mon-count-${s}`}>{summary[s] || 0}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ptcc-monitor-grid">
          {rows.length === 0 && <div className="text-slate-500 col-span-3 text-sm">No operators active yet.</div>}
          {rows.map(r => {
            const m = statusMeta[r.status] || statusMeta.offline;
            return (
              <div key={r.operator_id} className={`bg-white p-4 rounded-lg border shadow-sm ring-2 ${m.ring}`} data-testid={`mon-op-${r.operator_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-800">{r.operator_name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs text-white ${m.c}`}>{m.l}</span>
                </div>
                {r.status === 'in-call' && r.current_number && (
                  <div className="text-xs text-slate-500">
                    <div className="font-mono">📞 {r.current_number}</div>
                    {r.customer_name && <div>{r.customer_name}</div>}
                  </div>
                )}
                <div className="text-xs text-slate-400 mt-2">Last updated: {new Date(r.updated_at).toLocaleTimeString()}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );
}

// -----------------------------------------------------------------------
// ONBOARDING WIZARD — 3 steps for newly approved admins
// -----------------------------------------------------------------------
function OnboardingWizard() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/call/onboarding`, { headers: authHdr() });
      setStatus(r.data);
      if (r.data.complete) { nav('/admin', { replace: true }); return; }
      // Auto-advance to first incomplete step
      const s = r.data.steps || {};
      if (!s.sip_configured) setStep(0);
      else if (!s.has_list) setStep(1);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load onboarding status');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const skip = async () => {
    if (!window.confirm('Skip onboarding? You can always configure these later from the sidebar.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/call/onboarding/skip`, {}, { headers: authHdr() });
      nav('/admin', { replace: true });
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to skip'); }
  };

  const stepsMeta = [
    { key: 'sip_configured', title: 'Connect your SIP / voice provider', icon: Settings },
    { key: 'has_list',       title: 'Upload your first phone list',      icon: ListChecks },
  ];
  const s = status?.steps || {};
  const doneCount = Object.values(s).filter(Boolean).length;
  const pct = Math.round((doneCount / stepsMeta.length) * 100);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="ptcc-wizard">
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-emerald-400"/>
          <span className="font-semibold">Priority Talk — Getting started</span>
        </div>
        <button onClick={skip} className="text-sm text-slate-300 hover:text-white" data-testid="wizard-skip">
          Skip for now
        </button>
      </header>

      <main className="max-w-3xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold mb-1">Welcome aboard! 🎉</h1>
          <p className="text-slate-500 text-sm">Three quick steps to start dialing. You can revisit any of these from the sidebar.</p>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{doneCount} of {stepsMeta.length} complete</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div className="bg-emerald-500 h-2 transition-all" style={{ width: `${pct}%` }} data-testid="wizard-progress"/>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-6" data-testid="wizard-steps">
          {stepsMeta.map((m, i) => {
            const done = s[m.key];
            const active = i === step;
            return (
              <button
                key={m.key}
                onClick={() => setStep(i)}
                className={`flex-1 p-3 rounded-lg border text-left transition-all ${active ? 'border-emerald-500 bg-white shadow-sm' : done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                data-testid={`wizard-tab-${i}`}
              >
                <div className="flex items-center gap-2">
                  {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <Circle className="w-4 h-4 text-slate-400"/>}
                  <span className="text-xs uppercase text-slate-500">Step {i + 1}</span>
                </div>
                <div className="text-sm font-medium mt-1 line-clamp-1">{m.title}</div>
              </button>
            );
          })}
        </div>

        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400"/> : (
          <div className="bg-white rounded-lg border shadow-sm p-5" data-testid={`wizard-body-${step}`}>
            {err && <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}
            {step === 0 && <WizardStepSip done={s.sip_configured} onDone={refresh}/>}
            {step === 1 && <WizardStepList done={s.has_list} onDone={refresh}/>}
            <div className="flex justify-between items-center pt-4 mt-4 border-t">
              <button
                onClick={() => setStep(step - 1)}
                disabled={step === 0}
                className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 flex items-center gap-1"
                data-testid="wizard-prev"
              >
                <ArrowLeft className="w-4 h-4"/> Back
              </button>
              {step < stepsMeta.length - 1 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-1"
                  data-testid="wizard-next"
                >
                  Next <ArrowRight className="w-4 h-4"/>
                </button>
              ) : (
                <button
                  onClick={() => nav('/admin', { replace: true })}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-1"
                  data-testid="wizard-finish"
                >
                  Go to dashboard <ArrowRight className="w-4 h-4"/>
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function WizardStepSip({ done, onDone }) {
  const [cfg, setCfg] = useState({ sip_uri: '', ws_url: '', username: '', password: '', from_display: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/sip-config`, { headers: authHdr() }).then(r => {
      if (r.data?.configured) setCfg({
        sip_uri: r.data.sip_uri || '', ws_url: r.data.ws_url || '',
        username: r.data.username || '', password: r.data.password || '',
        from_display: r.data.from_display || '',
      });
    }).catch(() => {});
  }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/admin/call/sip-config`, cfg, { headers: authHdr() });
      await onDone();
    } catch (er) { setErr(er.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Settings className="w-5 h-5 text-slate-500"/>
        <h2 className="font-semibold text-lg">Connect your voice provider</h2>
        {done && <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">✓ Done</span>}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Your operators dial through the browser (WebRTC) using your own SIP account.
        Works with Twilio, Vonage, Plivo, Telnyx, Asterisk, FreeSWITCH, 3CX and any provider that offers SIP-over-WebSocket.
      </p>
      {err && <div className="p-2 mb-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}
      <form onSubmit={save} className="space-y-3">
        <SipField label="SIP URI" placeholder="sip:username@sip.provider.com" value={cfg.sip_uri} onChange={v => setCfg({...cfg, sip_uri: v})} testid="wsip-uri"/>
        <SipField label="WebSocket URL" placeholder="wss://sip.provider.com:7443" value={cfg.ws_url} onChange={v => setCfg({...cfg, ws_url: v})} testid="wsip-ws"/>
        <div className="grid grid-cols-2 gap-3">
          <SipField label="Username" value={cfg.username} onChange={v => setCfg({...cfg, username: v})} testid="wsip-username"/>
          <SipField label="Password" type="password" value={cfg.password} onChange={v => setCfg({...cfg, password: v})} testid="wsip-password"/>
        </div>
        <SipField label="Display name (optional)" placeholder="Acme Ltd" value={cfg.from_display} onChange={v => setCfg({...cfg, from_display: v})} testid="wsip-display"/>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-2" data-testid="wizard-sip-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin"/>} {done ? 'Update' : 'Save & continue'}
        </button>
      </form>
    </div>
  );
}

function WizardStepList({ done, onDone }) {
  const [name, setName] = useState('');
  const [numbers, setNumbers] = useState('');
  const [fileName, setFileName] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const parseCSV = (raw) => {
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const first = lines[0].toLowerCase();
    const startIdx = /(number|phone)/.test(first) && !/^\+?\d/.test(first) ? 1 : 0;
    return lines.slice(startIdx).map(line => {
      const parts = line.split(/[,;]/).map(p => p.replace(/^["']|["']$/g, '').trim());
      return { number: parts[0], name: parts[1] || null, notes: parts[2] || null };
    }).filter(e => e.number);
  };
  const readFile = (f) => {
    if (!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = ev => setNumbers(ev.target.result || '');
    r.readAsText(f);
    if (!name.trim()) setName(f.name.replace(/\.(csv|txt|tsv)$/i, ''));
  };
  const submit = async () => {
    if (!name.trim() || !numbers.trim()) return;
    setSaving(true); setErr(null);
    try {
      const entries = parseCSV(numbers);
      if (entries.length === 0) { setErr('No valid numbers found'); setSaving(false); return; }
      await axios.post(`${API_URL}/api/admin/call/lists`, { name: name.trim(), entries }, { headers: authHdr() });
      setName(''); setNumbers(''); setFileName(null);
      await onDone();
    } catch (e) { setErr(e.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="w-5 h-5 text-slate-500"/>
        <h2 className="font-semibold text-lg">Upload your first phone list</h2>
        {done && <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">✓ Done</span>}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Drop a CSV. First column is the phone number. Optional columns: name, notes.
        Operators will call these numbers in order from the top.
      </p>
      {err && <div className="p-2 mb-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}
      <input value={name} onChange={e => setName(e.target.value)} placeholder="List name (e.g. Q1 leads)" className="w-full px-3 py-2 border rounded mb-3" data-testid="wizard-list-name"/>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
        className={`border-2 border-dashed rounded-lg p-6 text-center mb-3 ${dragging ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}
        data-testid="wizard-list-drop"
      >
        <Upload className={`w-8 h-8 mx-auto mb-2 ${dragging ? 'text-emerald-600' : 'text-slate-400'}`}/>
        <p className="text-sm text-slate-600 mb-1">
          Drag &amp; drop a CSV file, or
          <label className="text-emerald-600 cursor-pointer underline ml-1">
            browse
            <input type="file" accept=".csv,.txt,.tsv" onChange={e => readFile(e.target.files?.[0])} className="hidden" data-testid="wizard-list-file"/>
          </label>
        </p>
        {fileName && <p className="text-xs text-slate-500">File: {fileName}</p>}
      </div>
      <button onClick={submit} disabled={saving || !name.trim() || !numbers.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-2" data-testid="wizard-list-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Create list
      </button>
    </div>
  );
}

function WizardStepBundle({ done, onDone }) {
  const [bundles, setBundles] = useState([]);
  const [buying, setBuying] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/call/bundles`, { headers: authHdr() })
      .then(r => setBundles(r.data.bundles || []))
      .catch(() => {});
  }, []);
  const buy = async (id) => {
    setBuying(id); setErr(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/call/purchase`,
        { bundle_id: id, success_url: `${window.location.origin}/admin/onboarding`, cancel_url: `${window.location.origin}/admin/onboarding` },
        { headers: authHdr() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { setErr(e.response?.data?.detail || 'Purchase failed'); setBuying(null); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCart className="w-5 h-5 text-slate-500"/>
        <h2 className="font-semibold text-lg">Buy your first minute bundle</h2>
        {done && <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">✓ Done</span>}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Minutes are deducted from your balance as your operators talk.
        Choose any bundle to get started — you can always top up later.
      </p>
      {err && <div className="p-2 mb-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{err}</div>}
      {bundles.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No bundles are available yet. Please contact the platform owner to create pricing bundles for you.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {bundles.map(b => (
            <div key={b.id} className="border rounded-lg p-3 hover:border-emerald-500 transition-colors" data-testid={`wizard-bundle-${b.id}`}>
              <div className="font-medium">{b.name}</div>
              <div className="text-2xl font-bold mt-1">{b.minutes} <span className="text-sm font-normal text-slate-500">min</span></div>
              <div className="text-sm mt-1">{b.price} {b.currency}</div>
              <button onClick={() => buy(b.id)} disabled={buying === b.id} className="w-full mt-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded disabled:opacity-50 flex items-center justify-center gap-1" data-testid={`wizard-buy-${b.id}`}>
                {buying === b.id && <Loader2 className="w-3 h-3 animate-spin"/>} Buy
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// MASTER SECURITY CENTER — WAF dashboard (Master-only)
// -----------------------------------------------------------------------
const SEVERITY_STYLE = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-300',
  HIGH:     'bg-orange-100 text-orange-700 border-orange-300',
  MEDIUM:   'bg-amber-100 text-amber-700 border-amber-300',
  LOW:      'bg-slate-100 text-slate-600 border-slate-200',
};

// -----------------------------------------------------------------------
// MASTER — Weekly accounting reports
// -----------------------------------------------------------------------
function MasterReports() {
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API_URL}/api/master/reports`, { headers: masterHdr() });
      setReports(r.data.reports || []);
    } catch (e) {
      if (e.response?.status === 401) { clearMasterSession(); nav('/master/login'); return; }
      setErr(e.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const regenerateThisWeek = async () => {
    if (!window.confirm('Regenerate this week\'s report with the latest data?')) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/master/reports/generate`, {}, { headers: masterHdr() });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const download = async (rep) => {
    try {
      const r = await axios.get(`${API_URL}/api/master/reports/${rep.id}/download`,
        { headers: masterHdr(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `priority-talk-weekly-${rep.week}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 100);
    } catch (e) { alert(e.response?.data?.detail || 'Download failed'); }
  };

  const logout = () => { clearMasterSession(); nav('/master/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="ptcc-master-reports">
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-400"/>
          <span className="font-semibold">Priority Talk — Weekly Reports</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/master')} className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
            <ArrowLeft className="w-4 h-4"/> Master console
          </button>
          <button onClick={logout} className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
            <LogOut className="w-4 h-4"/> Sign out
          </button>
        </div>
      </header>
      <main className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileText className="w-6 h-6"/> Weekly accounting reports
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Auto-generated PDF reports. This week&apos;s report is refreshed whenever this page loads.
            </p>
          </div>
          <button onClick={regenerateThisWeek} disabled={busy}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-2 disabled:opacity-50"
            data-testid="reports-regenerate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <PlayCircle className="w-4 h-4"/>}
            Regenerate this week
          </button>
        </div>

        {err && <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded" data-testid="reports-error">{err}</div>}

        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="reports-table">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left p-3">Week</th>
                <th className="text-left p-3">Period</th>
                <th className="text-right p-3">Revenue</th>
                <th className="text-right p-3">Tenants</th>
                <th className="text-left p-3">Generated</th>
                <th className="text-right p-3">Download</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline"/> Loading…</td></tr>
              )}
              {!loading && reports.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">No reports yet.</td></tr>
              )}
              {reports.map(rep => (
                <tr key={rep.id} className="border-t hover:bg-slate-50" data-testid={`reports-row-${rep.week}`}>
                  <td className="p-3 font-semibold">{rep.week}</td>
                  <td className="p-3 text-slate-600 text-xs">
                    {fmtDate(rep.period_start)} → {fmtDate(rep.period_end)}
                  </td>
                  <td className="p-3 text-right font-mono">{Number(rep.total_revenue || 0).toFixed(2)} {rep.currency || 'USD'}</td>
                  <td className="p-3 text-right">{rep.tenant_count} <span className="text-slate-400 text-xs">({rep.active_tenants} active)</span></td>
                  <td className="p-3 text-slate-500 text-xs">{fmtDate(rep.generated_at)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => download(rep)}
                      className="text-blue-600 hover:underline text-xs font-medium"
                      data-testid={`reports-download-${rep.week}`}>
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}




// -----------------------------------------------------------------------
// MASTER — Reseller (wholesale trunks + DID pool)
// -----------------------------------------------------------------------
function MasterReseller() {
  const nav = useNavigate();
  const [tab, setTab] = useState('dids'); // 'dids' | 'trunks'
  const logout = () => { clearMasterSession(); nav('/master/login', { replace: true }); };
  return (
    <div className="min-h-screen bg-slate-100" data-testid="master-reseller-page">
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-purple-400"/>
          <span className="font-semibold">Priority Talk — Reseller Console</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/master')} className="text-sm text-slate-300 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Master</button>
          <button onClick={logout} className="text-sm text-slate-300 hover:text-white flex items-center gap-1"><LogOut className="w-4 h-4"/> Sign out</button>
        </div>
      </header>
      <main className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2"><Phone className="w-6 h-6"/> Reseller: wholesale trunks & number pool</h1>
        <p className="text-sm text-slate-500 mb-6">
          Buy wholesale minutes and DIDs from carriers (Telnyx, 46elks, etc.), then let your tenants pick numbers from the shared pool.
        </p>
        <div className="mb-4 inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => setTab('dids')} className={`px-4 py-1.5 text-sm ${tab === 'dids' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700'}`} data-testid="tab-dids">Number pool</button>
          <button onClick={() => setTab('trunks')} className={`px-4 py-1.5 text-sm ${tab === 'trunks' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700'}`} data-testid="tab-trunks">Wholesale trunks</button>
          <button onClick={() => setTab('credits')} className={`px-4 py-1.5 text-sm ${tab === 'credits' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700'}`} data-testid="tab-credits">Credits</button>
          <button onClick={() => setTab('pricing')} className={`px-4 py-1.5 text-sm ${tab === 'pricing' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700'}`} data-testid="tab-pricing">Pricing</button>
        </div>
        {tab === 'dids' ? <ResellerDidsTab/> :
         tab === 'trunks' ? <ResellerTrunksTab/> :
         tab === 'credits' ? <ResellerCreditsTab/> :
         <ResellerPricingTab/>}
      </main>
    </div>
  );
}

function ResellerTrunksTab() {
  const [trunks, setTrunks] = useState([]);
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/master/wholesale-trunks`, { headers: masterHdr() }); setTrunks(r.data.trunks || []); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (t) => {
    if (!window.confirm(`Delete trunk "${t.name}"?`)) return;
    try { await axios.delete(`${API_URL}/api/master/wholesale-trunks/${t.id}`, { headers: masterHdr() }); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setShow(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center gap-2" data-testid="add-trunk-btn"><Settings className="w-4 h-4"/> Add wholesale trunk</button>
      </div>
      {err && <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left p-3">Name</th><th className="text-left p-3">Provider</th><th className="text-left p-3">SIP server</th><th className="text-left p-3">Username</th><th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {trunks.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No wholesale trunks yet. Add your Telnyx / 46elks / carrier account to start.</td></tr>}
            {trunks.map(t => (
              <tr key={t.id} className="border-t" data-testid={`trunk-${t.id}`}>
                <td className="p-3 font-semibold">{t.name}</td>
                <td className="p-3">{t.provider || '—'}</td>
                <td className="p-3 font-mono text-xs">{t.sip_server}</td>
                <td className="p-3 font-mono text-xs">{t.username}</td>
                <td className="p-3 text-right"><button onClick={() => remove(t)} className="text-xs text-red-600 hover:underline">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <AddTrunkModal onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }}/>}
    </div>
  );
}

function AddTrunkModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: '', sip_server: '', username: '', password: '', provider: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const applyPreset = (p) => {
    if (p === 'telnyx') setF(x => ({ ...x, provider: 'telnyx', sip_server: 'sip.telnyx.com', name: 'Telnyx' }));
    if (p === '46elks') setF(x => ({ ...x, provider: '46elks', sip_server: 'sip.46elks.com', name: '46elks' }));
    if (p === 'voipms') setF(x => ({ ...x, provider: 'voipms', sip_server: 'atlanta.voip.ms', name: 'VoIP.ms' }));
  };
  const save = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/wholesale-trunks`, f, { headers: masterHdr() });
      onCreated();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="add-trunk-modal">
        <h2 className="text-lg font-semibold mb-4">Add wholesale SIP trunk</h2>
        <div className="mb-3 flex gap-2">
          <span className="text-xs text-slate-600 self-center">Preset:</span>
          <button type="button" onClick={() => applyPreset('telnyx')} className="px-2 py-1 text-xs bg-blue-50 border border-blue-200 rounded" data-testid="preset-telnyx">Telnyx</button>
          <button type="button" onClick={() => applyPreset('46elks')} className="px-2 py-1 text-xs bg-blue-50 border border-blue-200 rounded" data-testid="preset-46elks">46elks</button>
          <button type="button" onClick={() => applyPreset('voipms')} className="px-2 py-1 text-xs bg-blue-50 border border-blue-200 rounded" data-testid="preset-voipms">VoIP.ms</button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <input placeholder="Name (e.g. Telnyx main)" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" data-testid="trunk-name"/>
          <input placeholder="SIP server (sip.telnyx.com)" value={f.sip_server} onChange={e => setF({...f, sip_server: e.target.value})} required className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="trunk-server"/>
          <input placeholder="Username (from carrier portal)" value={f.username} onChange={e => setF({...f, username: e.target.value})} required className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="trunk-username"/>
          <input type="password" placeholder="Password" value={f.password} onChange={e => setF({...f, password: e.target.value})} required className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="trunk-password"/>
          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">{err}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1" data-testid="trunk-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Save trunk
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResellerDidsTab() {
  const [dids, setDids] = useState([]);
  const [trunks, setTrunks] = useState([]);
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    try {
      const [dr, tr] = await Promise.all([
        axios.get(`${API_URL}/api/master/did-pool`, { headers: masterHdr() }),
        axios.get(`${API_URL}/api/master/wholesale-trunks`, { headers: masterHdr() }),
      ]);
      setDids(dr.data.dids || []); setTrunks(tr.data.trunks || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.number}?`)) return;
    try { await axios.delete(`${API_URL}/api/master/did-pool/${d.id}`, { headers: masterHdr() }); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const unassign = async (d) => {
    if (!window.confirm(`Take ${d.number} back from ${d.assigned_tenant_name}?`)) return;
    try { await axios.post(`${API_URL}/api/master/did-pool/${d.id}/unassign`, {}, { headers: masterHdr() }); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const stats = {
    total: dids.length,
    available: dids.filter(d => d.status === 'available').length,
    assigned: dids.filter(d => d.status === 'assigned').length,
    revenue: dids.filter(d => d.status === 'assigned').reduce((s, d) => s + (d.price_retail || 0), 0),
    cost: dids.reduce((s, d) => s + (d.cost_wholesale || 0), 0),
  };
  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCardColored label="Total numbers" value={stats.total}/>
        <StatCardColored label="Available" value={stats.available} color="emerald"/>
        <StatCardColored label="Assigned" value={stats.assigned} color="blue"/>
        <StatCardColored label="Monthly margin" value={`${(stats.revenue - stats.cost).toFixed(2)}`} sub={`revenue ${stats.revenue.toFixed(2)} − cost ${stats.cost.toFixed(2)}`} color="purple"/>
      </div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setShow(true)} disabled={trunks.length === 0} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center gap-2 disabled:opacity-50" title={trunks.length === 0 ? 'Add a wholesale trunk first' : ''} data-testid="add-did-btn"><Phone className="w-4 h-4"/> Add number</button>
      </div>
      {err && <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left p-3">Number</th><th className="text-left p-3">Status</th><th className="text-left p-3">Assigned to</th>
            <th className="text-right p-3">Cost / mo</th><th className="text-right p-3">Retail / mo</th><th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {dids.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No numbers in the pool yet. Buy DIDs from your carrier, then add them here.</td></tr>}
            {dids.map(d => (
              <tr key={d.id} className="border-t" data-testid={`did-${d.id}`}>
                <td className="p-3 font-mono">{d.number}</td>
                <td className="p-3">{d.status === 'assigned' ? <span className="text-blue-600 text-xs font-medium">● Assigned</span> : <span className="text-emerald-600 text-xs font-medium">● Available</span>}</td>
                <td className="p-3">{d.assigned_tenant_name || '—'}</td>
                <td className="p-3 text-right font-mono">{Number(d.cost_wholesale || 0).toFixed(2)}</td>
                <td className="p-3 text-right font-mono">{Number(d.price_retail || 0).toFixed(2)}</td>
                <td className="p-3 text-right space-x-2">
                  {d.status === 'assigned' && <button onClick={() => unassign(d)} className="text-xs text-amber-700 hover:underline">Unassign</button>}
                  {d.status !== 'assigned' && <button onClick={() => remove(d)} className="text-xs text-red-600 hover:underline">Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <AddDidModal trunks={trunks} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }}/>}
    </div>
  );
}

function StatCardColored({ label, value, sub, color = 'slate' }) {
  const bg = color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
             color === 'blue' ? 'bg-blue-50 border-blue-200' :
             color === 'purple' ? 'bg-purple-50 border-purple-200' :
             'bg-slate-50 border-slate-200';
  return (
    <div className={`p-4 rounded-lg border ${bg}`}>
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function AddDidModal({ trunks, onClose, onCreated }) {
  const [f, setF] = useState({ number: '', wholesale_trunk_id: trunks[0]?.id || '', cost_wholesale: '', price_retail: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const save = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/did-pool`, {
        ...f,
        cost_wholesale: parseFloat(f.cost_wholesale) || 0,
        price_retail: parseFloat(f.price_retail) || 0,
      }, { headers: masterHdr() });
      onCreated();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="add-did-modal">
        <h2 className="text-lg font-semibold mb-4">Add DID to pool</h2>
        <form onSubmit={save} className="space-y-3">
          <input placeholder="Number (E.164, e.g. +4712345678)" value={f.number} onChange={e => setF({...f, number: e.target.value})} required className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="did-number"/>
          <select value={f.wholesale_trunk_id} onChange={e => setF({...f, wholesale_trunk_id: e.target.value})} required className="w-full px-3 py-2 border rounded text-sm" data-testid="did-trunk">
            {trunks.map(t => <option key={t.id} value={t.id}>{t.name} ({t.sip_server})</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 block mb-1">Wholesale cost / mo</label>
              <input type="number" step="0.01" value={f.cost_wholesale} onChange={e => setF({...f, cost_wholesale: e.target.value})} className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="did-cost"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">Retail price / mo</label>
              <input type="number" step="0.01" value={f.price_retail} onChange={e => setF({...f, price_retail: e.target.value})} className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="did-retail"/>
            </div>
          </div>
          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">{err}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1" data-testid="did-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Add DID
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Reseller: Credits management (master credits tenants' prepaid balance)
// -----------------------------------------------------------------------
function ResellerCreditsTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [openTenant, setOpenTenant] = useState(null);

  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/master/all-balances`, { headers: masterHdr() }); setRows(r.data.rows || []); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const totalBalance = rows.reduce((s, r) => s + (r.balance_kr || 0), 0);
  const totalSpent = rows.reduce((s, r) => s + (r.spent_last_30d_kr || 0), 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCardColored label="Active tenants" value={rows.length}/>
        <StatCardColored label="Total outstanding balance" value={`${totalBalance.toFixed(2)} kr`} color="emerald"/>
        <StatCardColored label="Spent last 30 days" value={`${totalSpent.toFixed(2)} kr`} color="purple"/>
      </div>
      {err && <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="credits-table">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left p-3">Tenant</th>
            <th className="text-left p-3">Owner email</th>
            <th className="text-right p-3">Balance (kr)</th>
            <th className="text-right p-3">Spent 30d</th>
            <th className="text-center p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No tenants yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.tenant_id} className="border-t hover:bg-slate-50" data-testid={`credit-row-${r.tenant_id}`}>
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3 text-slate-600 text-xs">{r.owner_email}</td>
                <td className={`p-3 text-right font-mono font-semibold ${r.balance_kr <= 0 ? 'text-red-600' : r.balance_kr < 50 ? 'text-amber-600' : 'text-emerald-700'}`}>{r.balance_kr.toFixed(2)}</td>
                <td className="p-3 text-right font-mono text-slate-600">{r.spent_last_30d_kr.toFixed(2)}</td>
                <td className="p-3 text-center">
                  {r.blocked ? <span className="text-red-600 text-xs font-medium">● Blocked</span> : <span className="text-emerald-600 text-xs font-medium">● Active</span>}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => setOpenTenant(r)} className="text-xs text-purple-700 hover:underline" data-testid={`credit-manage-${r.tenant_id}`}>Add credit / History</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openTenant && <TenantCreditModal tenant={openTenant} onClose={() => setOpenTenant(null)} onChanged={() => { load(); }}/>}
    </div>
  );
}

function TenantCreditModal({ tenant, onClose, onChanged }) {
  const [data, setData] = useState({ balance_kr: tenant.balance_kr, transactions: [], loading: true });
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/master/tenants/${tenant.tenant_id}/credits`, { headers: masterHdr() });
      setData({ ...r.data, loading: false });
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt === 0) { setErr('Amount must be a non-zero number'); return; }
    setSaving(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/tenants/${tenant.tenant_id}/credits`,
        { amount_kr: amt, note }, { headers: masterHdr() });
      setAmount(''); setNote('');
      await load();
      onChanged();
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="credit-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">{tenant.name}</h2>
            <p className="text-xs text-slate-500">{tenant.owner_email}</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase">Current balance</div>
            <div className={`text-2xl font-semibold font-mono ${data.balance_kr <= 0 ? 'text-red-600' : 'text-emerald-700'}`}>{Number(data.balance_kr).toFixed(2)} kr</div>
            {data.blocked && <div className="text-xs text-red-600 mt-1">⚠ Calls blocked (balance depleted)</div>}
          </div>
        </div>

        <form onSubmit={submit} className="p-4 bg-purple-50 border border-purple-200 rounded mb-4">
          <div className="text-sm font-semibold text-purple-900 mb-2">Add credit (or subtract with negative number)</div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" step="0.01" placeholder="Amount (kr)" required value={amount} onChange={e => setAmount(e.target.value)}
              className="px-3 py-2 border rounded text-sm font-mono" data-testid="credit-amount"/>
            <input placeholder="Note (e.g. 'Bank transfer received #123')" value={note} onChange={e => setNote(e.target.value)}
              className="col-span-2 px-3 py-2 border rounded text-sm" data-testid="credit-note"/>
          </div>
          {err && <div className="mt-2 text-sm text-red-700">{err}</div>}
          <button type="submit" disabled={saving}
            className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1" data-testid="credit-submit">
            {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Apply
          </button>
        </form>

        <h3 className="text-sm font-semibold text-slate-700 mb-2">Transaction history</h3>
        <div className="border rounded overflow-hidden max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700 sticky top-0"><tr>
              <th className="text-left p-2">When</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Note</th>
              <th className="text-right p-2">Amount</th>
            </tr></thead>
            <tbody>
              {data.loading && <tr><td colSpan={4} className="p-4 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline"/></td></tr>}
              {!data.loading && data.transactions.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-slate-500">No transactions yet.</td></tr>}
              {data.transactions.map(tx => (
                <tr key={tx.id} className="border-t">
                  <td className="p-2 text-xs text-slate-500">{fmtDate(tx.created_at)}</td>
                  <td className="p-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${tx.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : tx.type === 'debit' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>{tx.type}</span>
                  </td>
                  <td className="p-2 text-xs">{tx.note || '—'}</td>
                  <td className={`p-2 text-right font-mono ${tx.amount_kr < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{tx.amount_kr > 0 ? '+' : ''}{Number(tx.amount_kr).toFixed(2)} kr</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded">Close</button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Reseller: Destination pricing (per-country per-minute rates)
// -----------------------------------------------------------------------
function ResellerPricingTab() {
  const [prices, setPrices] = useState([]);
  const [f, setF] = useState({ prefix: '', country_name: '', price_per_min_kr: '' });
  const [err, setErr] = useState(null);
  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/master/destination-prices`, { headers: masterHdr() }); setPrices(r.data.prices || []); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (region) => {
    const presets = {
      norway:  [{ prefix: '+47', country_name: 'Norway',        price: 0.15 }],
      nordic:  [{ prefix: '+46', country_name: 'Sweden',        price: 0.15 },
                { prefix: '+45', country_name: 'Denmark',       price: 0.15 },
                { prefix: '+358', country_name: 'Finland',      price: 0.20 }],
      europe:  [{ prefix: '+44', country_name: 'UK',            price: 0.20 },
                { prefix: '+49', country_name: 'Germany',       price: 0.20 },
                { prefix: '+33', country_name: 'France',        price: 0.20 }],
      us:      [{ prefix: '+1', country_name: 'US/Canada',      price: 0.10 }],
      fallback:[{ prefix: '*', country_name: 'All other',       price: 0.50 }],
    };
    const items = presets[region] || [];
    Promise.all(items.map(x => axios.post(`${API_URL}/api/master/destination-prices`,
      { prefix: x.prefix, country_name: x.country_name, price_per_min_kr: x.price }, { headers: masterHdr() })))
      .then(load);
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      await axios.post(`${API_URL}/api/master/destination-prices`,
        { prefix: f.prefix.trim(), country_name: f.country_name.trim(),
          price_per_min_kr: parseFloat(f.price_per_min_kr) },
        { headers: masterHdr() });
      setF({ prefix: '', country_name: '', price_per_min_kr: '' });
      load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed'); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete price for ${p.prefix}?`)) return;
    try { await axios.delete(`${API_URL}/api/master/destination-prices/${p.id}`, { headers: masterHdr() }); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3">
        <p className="text-xs font-semibold text-blue-900 mb-2">⚡ Quick add region defaults (edit prices after):</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => applyPreset('norway')} className="px-3 py-1 text-xs bg-white border rounded" data-testid="preset-norway">+ Norway 0.15 kr</button>
          <button onClick={() => applyPreset('nordic')} className="px-3 py-1 text-xs bg-white border rounded" data-testid="preset-nordic">+ Nordic</button>
          <button onClick={() => applyPreset('europe')} className="px-3 py-1 text-xs bg-white border rounded" data-testid="preset-europe">+ Europe</button>
          <button onClick={() => applyPreset('us')} className="px-3 py-1 text-xs bg-white border rounded" data-testid="preset-us">+ US/Canada</button>
          <button onClick={() => applyPreset('fallback')} className="px-3 py-1 text-xs bg-white border rounded" data-testid="preset-fallback">+ Fallback (*)</button>
        </div>
      </div>
      <form onSubmit={submit} className="mb-4 grid grid-cols-4 gap-2">
        <input required placeholder="Prefix (+47 or *)" value={f.prefix} onChange={e => setF({...f, prefix: e.target.value})} className="px-3 py-2 border rounded text-sm font-mono" data-testid="price-prefix"/>
        <input placeholder="Country name" value={f.country_name} onChange={e => setF({...f, country_name: e.target.value})} className="px-3 py-2 border rounded text-sm" data-testid="price-country"/>
        <input required type="number" step="0.01" placeholder="Price per min (kr)" value={f.price_per_min_kr} onChange={e => setF({...f, price_per_min_kr: e.target.value})} className="px-3 py-2 border rounded text-sm font-mono" data-testid="price-value"/>
        <button type="submit" className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm" data-testid="price-add">Add / Update</button>
      </form>
      {err && <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 rounded">{err}</div>}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="prices-table">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left p-3">Prefix</th>
            <th className="text-left p-3">Country</th>
            <th className="text-right p-3">Price / min (kr)</th>
            <th className="text-right p-3"></th>
          </tr></thead>
          <tbody>
            {prices.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No prices set. Add prefixes so calls get billed correctly. Use * as fallback.</td></tr>}
            {prices.map(p => (
              <tr key={p.id} className="border-t" data-testid={`price-${p.prefix}`}>
                <td className="p-3 font-mono">{p.prefix}</td>
                <td className="p-3">{p.country_name || '—'}</td>
                <td className="p-3 text-right font-mono">{Number(p.price_per_min_kr).toFixed(4)}</td>
                <td className="p-3 text-right"><button onClick={() => del(p)} className="text-xs text-red-600 hover:underline">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MasterSecurity() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [wsLive, setWsLive] = useState(false);
  const [manualIp, setManualIp] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const wsRef = useRef(null);

  const load = async () => {
    setErr(null);
    try {
      const [d, e, b] = await Promise.all([
        axios.get(`${API_URL}/api/master/security/dashboard`, { headers: masterHdr() }),
        axios.get(`${API_URL}/api/master/security/events?limit=200`, { headers: masterHdr() }),
        axios.get(`${API_URL}/api/master/security/blacklist`, { headers: masterHdr() }),
      ]);
      setStats(d.data);
      setEvents(e.data.events || []);
      setBlacklist(b.data.blacklist || []);
    } catch (e2) {
      if (e2.response?.status === 401) { clearMasterSession(); nav('/master/login'); return; }
      setErr(e2.response?.data?.detail || 'Failed to load security data');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  // Live WebSocket for instant threat feed
  useEffect(() => {
    const tok = getMasterToken();
    if (!tok) return;
    let closed = false;
    let retry = null;
    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(`${WS_BASE}/ws/master/security?token=${encodeURIComponent(tok)}`);
      wsRef.current = ws;
      ws.onopen = () => setWsLive(true);
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === 'security_event' && d.event) {
            setEvents(prev => [d.event, ...prev].slice(0, 500));
          } else if (d.type === 'auto_block' && d.ip) {
            setBlacklist(prev => (prev.some(b => b.ip === d.ip) ? prev : [{ ip: d.ip, auto_blocked: true, reason: d.reason, blocked_at: d.at || new Date().toISOString(), blocked_by: 'NSA_AUTO_BLOCK', threat_score: d.threat_score }, ...prev]));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { setWsLive(false); if (!closed) retry = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; if (retry) clearTimeout(retry); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  const blockIp = async () => {
    if (!manualIp.trim()) return;
    setManualBusy(true);
    try {
      await axios.post(`${API_URL}/api/master/security/blacklist`,
        { ip: manualIp.trim(), reason: manualReason.trim() || 'manual block by master' },
        { headers: masterHdr() });
      setManualIp(''); setManualReason('');
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Block failed'); }
    finally { setManualBusy(false); }
  };
  const unblock = async (ip) => {
    if (!window.confirm(`Unblock ${ip}? They will regain access to all sites.`)) return;
    try {
      await axios.delete(`${API_URL}/api/master/security/blacklist/${encodeURIComponent(ip)}`,
        { headers: masterHdr() });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Unblock failed'); }
  };
  const logout = () => { clearMasterSession(); nav('/master/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ptcc-master-security">
      <header className="bg-black border-b border-red-900/40 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-400"/>
          <span className="font-semibold">Priority Talk — Security Center</span>
          <span className={`ml-4 text-xs px-2 py-0.5 rounded border ${wsLive ? 'border-emerald-500 text-emerald-400' : 'border-slate-600 text-slate-500'}`} data-testid="waf-ws-status">
            <Activity className="w-3 h-3 inline mr-1"/> {wsLive ? 'LIVE' : 'polling'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/master')} className="text-sm text-slate-300 hover:text-white" data-testid="waf-back">
            ← Companies
          </button>
          <button onClick={logout} className="text-sm text-slate-300 hover:text-white flex items-center gap-1" data-testid="waf-logout">
            <LogOut className="w-4 h-4"/> Sign out
          </button>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-red-400"/> Threat Operations Center
        </h1>
        <p className="text-sm text-slate-400 mb-6">
          Real-time WAF firewall. Every request across every company is inspected for SSRF, XSS, SQL/NoSQL injection, scanner UAs, brute-force logins and rate-limit abuse. IPs that exceed the threat-score threshold are <b>auto-blocked</b> from the entire platform.
        </p>

        {err && <div className="p-3 mb-4 bg-red-950/40 border border-red-800 text-red-300 rounded" data-testid="waf-error">{err}</div>}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <SecStat label="Events · 24h" value={stats?.totals?.events_24h ?? '—'} icon={Activity} color="text-slate-100" testid="waf-events-24h"/>
          <SecStat label="Events · last hour" value={stats?.totals?.events_1h ?? '—'} icon={Zap} color="text-amber-400" testid="waf-events-1h"/>
          <SecStat label="Critical · 24h" value={stats?.totals?.critical_24h ?? '—'} icon={AlertCircle} color="text-red-400" testid="waf-critical"/>
          <SecStat label="Blocked IPs" value={stats?.totals?.blocked_ips ?? '—'} icon={Ban} color="text-red-400" testid="waf-blocked"/>
          <SecStat label="Auto-blocked" value={stats?.totals?.auto_blocked_ips ?? '—'} icon={ShieldCheck} color="text-emerald-400" testid="waf-auto-blocked"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Top event types */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
            <h2 className="font-medium mb-3 flex items-center gap-2 text-slate-200"><Zap className="w-4 h-4 text-amber-400"/> Top attack types (24h)</h2>
            {(stats?.top_event_types || []).length === 0 && <div className="text-sm text-slate-500 italic">No attacks in the last 24 hours. All quiet.</div>}
            <div className="space-y-1.5" data-testid="waf-top-events">
              {(stats?.top_event_types || []).map(t => (
                <div key={t.event_type} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-slate-300">{t.event_type}</span>
                  <span className="px-2 py-0.5 rounded bg-red-950 text-red-300 text-xs">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Top offender IPs */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
            <h2 className="font-medium mb-3 flex items-center gap-2 text-slate-200"><Globe className="w-4 h-4 text-red-400"/> Top offender IPs (24h)</h2>
            {(stats?.top_offender_ips || []).length === 0 && <div className="text-sm text-slate-500 italic">—</div>}
            <div className="space-y-1.5" data-testid="waf-top-ips">
              {(stats?.top_offender_ips || []).map(t => (
                <div key={t.ip} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono text-slate-300">{t.ip}</span>
                    <span className="ml-2 text-xs text-slate-500">score {t.max_threat_score}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-red-950 text-red-300 text-xs">{t.count} events</span>
                    <button onClick={() => { setManualIp(t.ip); setManualReason(`top offender · last: ${t.last_event}`); }}
                      className="text-xs text-slate-400 hover:text-red-400" title="Copy to manual block">
                      → block
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Manual block */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 mb-6" data-testid="waf-manual-block">
          <h2 className="font-medium mb-3 flex items-center gap-2 text-slate-200"><Ban className="w-4 h-4 text-red-400"/> Manual IP block</h2>
          <div className="flex flex-wrap gap-2">
            <input value={manualIp} onChange={e => setManualIp(e.target.value)} placeholder="1.2.3.4 or 2001:db8::"
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm font-mono w-48" data-testid="waf-manual-ip"/>
            <input value={manualReason} onChange={e => setManualReason(e.target.value)} placeholder="Reason (optional)"
              className="flex-1 min-w-[200px] px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm" data-testid="waf-manual-reason"/>
            <button onClick={blockIp} disabled={manualBusy || !manualIp.trim()}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1" data-testid="waf-manual-submit">
              {manualBusy && <Loader2 className="w-4 h-4 animate-spin"/>}
              <Ban className="w-4 h-4"/> Block IP
            </button>
          </div>
        </div>

        {/* Blocked IPs */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 mb-6">
          <h2 className="font-medium mb-3 flex items-center gap-2 text-slate-200"><Ban className="w-4 h-4 text-red-400"/> Currently blocked IPs ({blacklist.length})</h2>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left p-2">IP</th>
                  <th className="text-left p-2">Blocked by</th>
                  <th className="text-left p-2">Reason</th>
                  <th className="text-left p-2">Country</th>
                  <th className="text-right p-2">Score</th>
                  <th className="text-left p-2">Blocked at</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="waf-blacklist-tbody">
                {blacklist.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500 italic">No IPs blocked yet.</td></tr>}
                {blacklist.map(b => (
                  <tr key={b.ip} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`waf-blocked-${b.ip}`}>
                    <td className="p-2 font-mono text-red-300">{b.ip}</td>
                    <td className="p-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${b.auto_blocked ? 'bg-red-950 text-red-300' : 'bg-slate-800 text-slate-300'}`}>
                        {b.auto_blocked ? 'AUTO' : (b.blocked_by || 'master')}
                      </span>
                    </td>
                    <td className="p-2 text-slate-400 text-xs max-w-xs truncate" title={b.reason}>{b.reason || '—'}</td>
                    <td className="p-2 text-slate-400 text-xs">{b.country || '—'}</td>
                    <td className="p-2 text-right font-mono text-amber-400 text-xs">{b.threat_score ?? 0}</td>
                    <td className="p-2 text-slate-500 text-xs">{b.blocked_at ? new Date(b.blocked_at).toLocaleString() : '—'}</td>
                    <td className="p-2 text-right">
                      <button onClick={() => unblock(b.ip)} className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1 ml-auto" data-testid={`waf-unblock-${b.ip}`}>
                        <Trash2 className="w-3 h-3"/> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Live event feed */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
          <h2 className="font-medium mb-3 flex items-center gap-2 text-slate-200">
            <Activity className="w-4 h-4 text-emerald-400"/> Live threat feed
            <span className="ml-auto text-xs text-slate-500">{events.length} events</span>
          </h2>
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500"/> : (
            <div className="overflow-auto max-h-[500px]">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase sticky top-0 bg-slate-900">
                  <tr>
                    <th className="text-left p-2">Time</th>
                    <th className="text-left p-2">Severity</th>
                    <th className="text-left p-2">Event</th>
                    <th className="text-left p-2">IP</th>
                    <th className="text-left p-2">Country</th>
                    <th className="text-left p-2">Details</th>
                    <th className="text-right p-2">Score</th>
                  </tr>
                </thead>
                <tbody data-testid="waf-events-tbody">
                  {events.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500 italic">No security events yet.</td></tr>}
                  {events.map(e => (
                    <tr key={`${e.ip}-${e.created_at}-${e.event_type}`} className="border-t border-slate-800/60">
                      <td className="p-2 text-slate-500 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleTimeString() : '—'}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded border text-[10px] ${SEVERITY_STYLE[e.severity] || SEVERITY_STYLE.LOW}`}>
                          {e.severity || 'LOW'}
                        </span>
                      </td>
                      <td className="p-2 font-mono text-slate-300">{e.event_type}</td>
                      <td className="p-2 font-mono text-red-300">{e.ip}</td>
                      <td className="p-2 text-slate-400 whitespace-nowrap">{e.country || '—'}</td>
                      <td className="p-2 text-slate-500 max-w-md truncate" title={e.details}>{e.details || '—'}</td>
                      <td className="p-2 text-right font-mono text-amber-400">{e.threat_score ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SecStat({ label, value, icon: Icon, color = 'text-slate-100', testid }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------
// ROOT APP
// -----------------------------------------------------------------------
export default function CallCenterApp() {
  useEffect(() => { document.title = 'Priority Call Center'; }, []);
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={getToken() ? <Navigate to={getRole() === 'super_admin' ? '/admin' : '/operator'} replace/> : <Login/>}/>
        <Route path="/signup" element={<Signup/>}/>

        {/* Master */}
        <Route path="/master/login"    element={<MasterLogin/>}/>
        <Route path="/master"          element={<MasterGuard><MasterDashboard/></MasterGuard>}/>
        <Route path="/master/security" element={<MasterGuard><MasterSecurity/></MasterGuard>}/>
        <Route path="/master/reports"  element={<MasterGuard><MasterReports/></MasterGuard>}/>
        <Route path="/master/reseller" element={<MasterGuard><MasterReseller/></MasterGuard>}/>

        {/* Tenant admin */}
        <Route path="/admin"              element={<Guard requireRole="super_admin"><Overview/></Guard>}/>
        <Route path="/admin/onboarding"   element={<Guard requireRole="super_admin"><OnboardingWizard/></Guard>}/>
        <Route path="/admin/monitor"      element={<Guard requireRole="super_admin"><LiveMonitor/></Guard>}/>
        <Route path="/admin/lists"        element={<Guard requireRole="super_admin"><Lists/></Guard>}/>
        <Route path="/admin/records"      element={<Guard requireRole="super_admin"><Records/></Guard>}/>
        <Route path="/admin/leaderboard"  element={<Guard requireRole="super_admin"><Leaderboard/></Guard>}/>
        <Route path="/admin/sip"          element={<Guard requireRole="super_admin"><SipConfig/></Guard>}/>
        <Route path="/admin/team"         element={<Guard requireRole="super_admin"><TeamManagement/></Guard>}/>

        {/* Operator */}
        <Route path="/operator"           element={<Guard><Shift/></Guard>}/>
        <Route path="/team-chat"          element={<Guard><TeamChat/></Guard>}/>

        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Routes>
    </BrowserRouter>
  );
}
